import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { TaskDisclosure } from "../../components/TaskDisclosure";
import { downloadReceiptDocument } from "../../lib/receiptDocument";
import {
  formatMembershipBilling,
  formatMembershipTerm
} from "./formatMembership";
import {
  APPLICATION_QUESTION_TYPES,
  FIREARMS_SCREENING_TEMPLATE,
  applicationAnswerDisplay,
  applicationFormIsPublished,
  applicationFormQuestionCount,
  applicationFormSectionsWithResponses,
  normalizeApplicationFormForClient,
  slugifyFormKey
} from "./applicationForm";

const APPLICANT_FILTERS = [
  { value: "pending_review", label: "Needs review" },
  { value: "all", label: "All applicants" },
  { value: "needs_waiver", label: "Needs waiver" },
  { value: "needs_prequalification", label: "Needs qualification" },
  { value: "needs_info", label: "Needs info" },
  { value: "ready", label: "Ready to approve" },
  { value: "rejected", label: "Rejected" },
  { value: "active", label: "Active" }
];

function csvCell(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function nonNegativeNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0;
}

function positiveInteger(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0;
}

function prequalificationResponseSummary(member) {
  const checklist = member.review?.requirements?.prequalification?.checklist || [];
  return checklist
    .filter((step) => step.applicantResponse)
    .map((step) => `${step.label}: ${step.applicantResponse}`)
    .join(" | ");
}

function datetimeLocalDaysFromNow(days) {
  const count = Number.parseInt(days || 0, 10);
  if (!Number.isFinite(count) || count <= 0) {
    return "";
  }

  const date = new Date();
  date.setDate(date.getDate() + count);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

function toDatetimeLocal(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

function memberSortTime(member) {
  return new Date(member.updatedAt || member.createdAt || 0).getTime() || 0;
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString() : "Not recorded";
}

function compactValue(value) {
  return reviewStatusCopy(value, "Not recorded");
}

function memberHasActiveAccess(member = {}) {
  const effectiveStatus = member.expiration?.effectiveStatus || member.status;
  const reviewStatus = member.reviewSummary?.reviewStatus || member.review?.status;
  return effectiveStatus === "active" && (!reviewStatus || reviewStatus === "approved");
}

function isDemoMemberRecord(member = {}) {
  const email = String(member.email || "").toLowerCase();
  const name = String(member.fullName || "").toLowerCase();
  const source = String(member.source || "").toLowerCase();
  return /(?:@dev\.rangegrid\.local|rangegrid-.*audit|owner-audit|test@|example\.com)/i.test(email)
    || /(?:demo|audit|test)/i.test(name)
    || /(?:codex|audit|test)/i.test(source);
}

function reviewStatusCopy(value, fallback = "Not required") {
  const cleaned = String(value || "").trim();
  if (!cleaned) {
    return fallback;
  }
  return cleaned
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatDateOnly(value) {
  return value ? new Date(value).toLocaleDateString() : "Not set";
}

function shortReference(value) {
  if (!value) {
    return "";
  }
  const text = String(value);
  return text.length > 24 ? `${text.slice(0, 12)}...${text.slice(-8)}` : text;
}

function documentTypeLabel(value) {
  const labels = {
    waiver: "Waiver",
    prequalification: "Qualification",
    id: "Identity document",
    other: "Other document"
  };
  return labels[value] || reviewStatusCopy(value, "Document");
}

function documentReferenceLabel(value) {
  if (!value) {
    return "";
  }
  try {
    const parsed = new URL(String(value));
    return parsed.hostname ? `Secure file linked from ${parsed.hostname}` : "Secure file linked";
  } catch {
    return "Internal document reference saved";
  }
}

function formatExpiration(member) {
  const expiration = member.expiration || {};
  if (!expiration.currentTermEnd) {
    return "No term end";
  }
  if (expiration.expiredByTerm) {
    return `Term expired ${formatDateTime(expiration.currentTermEnd)}`;
  }
  if (expiration.expiringSoon) {
    return `Expires in ${expiration.expiresInDays} day${expiration.expiresInDays === 1 ? "" : "s"}`;
  }
  return `Term ends ${formatDateTime(expiration.currentTermEnd)}`;
}

function formatTimelineLabel(item) {
  const key = String(item?.type || item?.action || "").trim();
  const labels = {
    "member.created": "Member created",
    "member.approve": "Member approved",
    "member.reject": "Member rejected",
    "member.review_updated": "Review updated",
    "membership.signup_received": "Application received",
    "membership.signup_approved": "Application approved",
    "membership.waiver_acknowledged": "Waiver acknowledged",
    "membership.prequalification_submitted": "Application submitted",
    "membership.payment_fulfilled": "Membership payment fulfilled",
    "membership_signup_intent_created": "Application started",
    "membership_signup_intent.fulfilled": "Membership payment fulfilled",
    "member.subscription_lifecycle_updated": "Subscription updated",
    "event_registration.payment_fulfilled": "Event payment fulfilled",
    "payment.checkout_completed": "Checkout completed",
    "membership_purchase": "Membership receipt",
    "event_registration": "Event registration receipt",
    "resource_reservation": "Reservation receipt",
    "resource.reservation_created": "Reservation created",
    "resource.reservation_rescheduled": "Reservation rescheduled",
    "resource.reservation_cancelled": "Reservation cancelled",
    "resource.reservation_completed": "Reservation completed",
    "resource.reservation_no_show": "Reservation no-show",
    "resource_reservation.member_created": "Reservation created",
    "resource_reservation.member_rescheduled": "Reservation rescheduled",
    "resource_reservation.member_cancelled": "Reservation cancelled",
    "resource_reservation.updated": "Reservation updated",
    "resource_reservation.confirmed": "Reservation confirmed",
    "resource_reservation.completed": "Reservation completed",
    "resource_reservation.no_show": "Reservation no-show",
    "resource_reservation.cancelled": "Reservation cancelled"
  };
  return labels[key] || reviewStatusCopy(key, "Timeline event");
}

function timelineSourceLabel(value) {
  const labels = {
    payment_receipt: "Payment record",
    reservation: "Reservation",
    membership: "Membership",
    audit: "Staff update",
    notification: "Message"
  };
  return labels[value] || reviewStatusCopy(value, "Range record");
}

function formatTimelineDetail(item = {}) {
  const details = item.details || {};
  if (item.source === "payment_receipt") {
    return `${receiptAmount(details)} / ${details.description || details.type || "Payment receipt"}`;
  }
  if (item.source === "reservation") {
    return `${details.resourceSnapshot?.name || "Reservation resource"} / ${reviewStatusCopy(details.status, "Reservation")}`;
  }
  return "";
}

function slugifyRequirementKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

function checklistItemsFromText(value) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [label, instructions] = line.split("|").map((item) => item.trim());
      return {
        id: `${index}-${slugifyRequirementKey(label) || "step"}`,
        label,
        instructions: instructions || ""
      };
    });
}

function checklistItemsToText(items = []) {
  return items
    .map((item) => [
      item.label,
      item.instructions
    ].filter(Boolean).join(" | "))
    .filter(Boolean)
    .join("\n");
}

function qualificationItemsFromText(value) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [label, key, defaultExpiresInDays, description] = line.split("|").map((item) => item.trim());
      return {
        id: `${index}-${slugifyRequirementKey(key || label) || "qualification"}`,
        label,
        key,
        defaultExpiresInDays,
        description
      };
    });
}

function qualificationItemsToText(items = []) {
  return items
    .map((item) => [
      item.label,
      item.key,
      item.defaultExpiresInDays,
      item.description
    ].map((part) => String(part || "").trim()).join(" | "))
    .filter((line) => line.replace(/[|\s]/g, ""))
    .join("\n");
}

function normalizeQualificationKey(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function accessCredentialStatusLabel(status) {
  const labels = {
    not_issued: "Not issued",
    ready_to_issue: "Ready to issue",
    issued: "Issued",
    active: "Active",
    disabled: "Disabled",
    lost: "Lost",
    returned: "Returned"
  };
  return labels[status] || "Not issued";
}

function formatGuestPassBalance(ledger = {}) {
  return `${ledger.remaining || 0} remaining / ${ledger.used || 0} used`;
}

function receiptAmount(receipt = {}) {
  if (receipt.amountCents === undefined || receipt.amountCents === null) {
    return "Amount pending";
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: receipt.currency || "usd"
  }).format(Number(receipt.amountCents || 0) / 100);
}

function formatCurrency(amountCents = 0, currency = "usd") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "usd"
  }).format(Number(amountCents || 0) / 100);
}

function chapterLabel(chapter = {}) {
  return [chapter.name, [chapter.city, chapter.region].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(" / ");
}

function chapterNameById(chapters = [], chapterId = "") {
  return chapterLabel(chapters.find((chapter) => chapter.chapterId === chapterId)) || (chapterId ? "Selected chapter" : "");
}

export function MembershipPage({
  addingHouseholdMemberId,
  addingMemberDocumentId,
  addingMemberFlagId,
  addingMemberQualificationId,
  archivingMembershipTypeId,
  chapters = [],
  chaptersEnabled = false,
  chaptersLoading = false,
  changingMemberPlanId,
  createMemberForm,
  editingMembershipTypeId,
  loading,
  membershipConfigForm,
  memberDetail,
  memberDetailLoading,
  memberImportCommitting,
  memberImportLoading,
  memberImportPreview,
  memberPaymentEvents,
  memberPaymentEventsLoading,
  memberPaymentReceipts,
  memberPaymentReceiptsLoading,
  memberFilters,
  memberTimeline,
  memberTimelineLoading,
  members,
  membersLoading,
  onAddHouseholdMember,
  onAddMemberDocument,
  onAddMemberFlag,
  onAddMemberQualification,
  onChangeMemberPlan,
  onCloseMemberDetail,
  onCommitMemberImport,
  onCreateMember,
  onArchiveMembershipType,
  onEditMembershipType,
  onExportMemberFlags,
  onExportMemberGuestPasses,
  onExportMemberHousehold,
  onExportMemberQualifications,
  onExportMembers,
  onOpenMemberDetail,
  onPreviewMemberImport,
  onRenewMember,
  onUpdateMemberGuestPasses,
  onResetMembershipTypeForm,
  onSaveMembershipConfig,
  onSaveMembershipType,
  onSetDefaultMembershipType,
  onRefreshStripeConnect,
  onStartStripeConnect,
  onUpdateResource,
  onUploadWaiver,
  onUpdateMemberReview,
  onUpdateMemberQualification,
  onUpdateMemberProfile,
  onUpdateMemberStatus,
  paymentSettings,
  paymentsLoading,
  renewingMemberId,
  refreshingStripeConnect,
  resources = [],
  resourcesEnabled = false,
  selectedRange,
  setCreateMemberForm,
  setMemberFilters,
  setMembershipConfigForm,
  setMembershipTypeForm,
  summary,
  membershipTypeForm,
  savingMembershipConfig,
  savingMembershipType,
  savingMember,
  selectedMemberDetailId,
  startingStripeConnect,
  uploadingWaiver,
  updatingGuestPassMemberId,
  updatingMemberProfileId,
  updatingMemberStatusId,
  updatingMemberId,
  updatingResourceId,
  exportingMemberFlags,
  exportingMemberGuestPasses,
  exportingMemberHousehold,
  exportingMemberQualifications,
  exportingMembers
}) {
  const location = useLocation();
  const [applicantFilter, setApplicantFilter] = useState("pending_review");
  const [applicantSearch, setApplicantSearch] = useState("");
  const visibleMembers = (members || []).filter((member) => !isDemoMemberRecord(member));
  const hiddenDemoMemberCount = Math.max(0, (members || []).length - visibleMembers.length);
  const pendingQualificationRequests = visibleMembers.flatMap((member) => (
    (member.qualifications?.items || [])
      .filter((qualification) => qualification.status === "pending" && qualification.source === "public_request")
      .map((qualification) => ({ member, qualification }))
  ));
  const [reviewDrafts, setReviewDrafts] = useState({});
  const [renewalDrafts, setRenewalDrafts] = useState({});
  const [planChangeDrafts, setPlanChangeDrafts] = useState({});
  const [guestPassDrafts, setGuestPassDrafts] = useState({});
  const [profileDrafts, setProfileDrafts] = useState({});
  const [statusDrafts, setStatusDrafts] = useState({});
  const [householdDrafts, setHouseholdDrafts] = useState({});
  const [documentDrafts, setDocumentDrafts] = useState({});
  const [flagDrafts, setFlagDrafts] = useState({});
  const [flagSeverityFilter, setFlagSeverityFilter] = useState("");
  const [flagStatusFilter, setFlagStatusFilter] = useState("active");
  const [qualificationDrafts, setQualificationDrafts] = useState({});
  const [memberImportCsv, setMemberImportCsv] = useState("");
  const [memberRosterAction, setMemberRosterAction] = useState("");
  const [memberExportChooserOpen, setMemberExportChooserOpen] = useState(false);
  const [planEditorExpanded, setPlanEditorExpanded] = useState(false);
  const planEditorWasSavingRef = useRef(false);
  const [memberDetailMode, setMemberDetailMode] = useState("overview");
  const [memberActivityExpanded, setMemberActivityExpanded] = useState({
    notifications: false,
    timeline: false
  });
  const [requirementsSubsection, setRequirementsSubsection] = useState("entry");
  const [requirementsAddOpen, setRequirementsAddOpen] = useState(false);
  const [requirementsEditTarget, setRequirementsEditTarget] = useState("");
  const [accessResourceFilter, setAccessResourceFilter] = useState("");
  const [accessResourceTypeFilter, setAccessResourceTypeFilter] = useState("");
  const [requirementGateDraft, setRequirementGateDraft] = useState({
    type: "onboarding",
    label: "",
    key: "",
    expiresInDays: "365",
    note: ""
  });
  const [activeMembershipSection, setActiveMembershipSection] = useState(() => {
    if (typeof window === "undefined") {
      return "plans";
    }
    const hash = window.location.hash.replace("#membership-", "");
    return ["plans", "members", "reviews", "application", "requirements"].includes(hash) ? hash : "plans";
  });
  useEffect(() => {
    function syncMembershipSectionFromHash() {
      const hash = window.location.hash.replace("#membership-", "");
      if (["plans", "members", "reviews", "application", "requirements"].includes(hash)) {
        setActiveMembershipSection(hash);
      }
    }

    syncMembershipSectionFromHash();
    window.addEventListener("hashchange", syncMembershipSectionFromHash);
    return () => window.removeEventListener("hashchange", syncMembershipSectionFromHash);
  }, []);

  useEffect(() => {
    const hash = location.hash.replace("#membership-", "");
    if (["plans", "members", "reviews", "requirements"].includes(hash)) {
      setActiveMembershipSection(hash);
    }
  }, [location.hash]);

  useEffect(() => {
    setMemberDetailMode("overview");
    setMemberActivityExpanded({ notifications: false, timeline: false });
  }, [selectedMemberDetailId]);

  useEffect(() => {
    if (
      planEditorWasSavingRef.current &&
      !savingMembershipType &&
      !editingMembershipTypeId &&
      !membershipTypeForm.name.trim()
    ) {
      setPlanEditorExpanded(false);
    }
    planEditorWasSavingRef.current = savingMembershipType;
  }, [editingMembershipTypeId, membershipTypeForm.name, savingMembershipType]);

  if (!selectedRange) {
    return null;
  }

  const membershipTypes = summary?.membershipTypes || [];
  const activeMembershipTypes = membershipTypes.filter((type) => type.status === "active");
  const defaultMembershipTypeId = summary?.config?.defaultMembershipTypeId;
  const editingMembershipType = editingMembershipTypeId
    ? membershipTypes.find((type) => type.membershipTypeId === editingMembershipTypeId)
    : null;
  const applicantMembers = visibleMembers.filter((member) => (
    member.planChangeRequest?.status === "pending" ||
    member.status === "pending" ||
    ["pending", "needs_info", "rejected"].includes(member.review?.status)
  ));
  const pendingMembers = applicantMembers.filter((member) => (
    member.status === "pending" || member.planChangeRequest?.status === "pending"
  ));
  const activeMembers = visibleMembers.filter((member) => !applicantMembers.includes(member));
  const activeAccessMembers = visibleMembers.filter(memberHasActiveAccess);
  const planEditorOpen = membershipTypes.length === 0 || Boolean(editingMembershipTypeId) || planEditorExpanded;
  const memberExportBusy = exportingMembers ||
    exportingMemberQualifications ||
    exportingMemberFlags ||
    exportingMemberHousehold ||
    exportingMemberGuestPasses;
  const stripePayment = paymentSettings?.stripe || {};
  const stripeReady = stripePayment.onboardingStatus === "complete" && stripePayment.chargesEnabled;
  const stripeStatusLabel = stripeReady
    ? "Ready"
    : stripePayment.connectedAccountId
      ? "Onboarding needed"
      : paymentSettings?.configured === false
        ? "Payment setup needed"
        : "Not connected";
  const paidPublicPlanBlocked = membershipTypeForm.publicSignupEnabled &&
    Number.parseFloat(membershipTypeForm.price || "0") > 0 &&
    !stripeReady;
  const planSaveIssues = [];
  if (!membershipTypeForm.name.trim()) {
    planSaveIssues.push("Plan name is required.");
  }
  if (!nonNegativeNumber(membershipTypeForm.price)) {
    planSaveIssues.push("Price must be zero or higher.");
  }
  if (membershipTypeForm.paymentMode === "prescreen_then_balance") {
    const fullPrice = Number.parseFloat(membershipTypeForm.price || "0");
    const prescreeningFee = Number.parseFloat(membershipTypeForm.prescreeningFee || "0");
    if (!Number.isFinite(prescreeningFee) || prescreeningFee <= 0) {
      planSaveIssues.push("Prescreening fee must be greater than zero.");
    } else if (Number.isFinite(fullPrice) && prescreeningFee >= fullPrice) {
      planSaveIssues.push("Prescreening fee must be less than the full membership price.");
    }
    if (!positiveInteger(membershipTypeForm.balanceDueDays)) {
      planSaveIssues.push("Balance due window is required for prescreened plans.");
    }
  }
  if (membershipTypeForm.termUnit !== "lifetime" && !positiveInteger(membershipTypeForm.termCount)) {
    planSaveIssues.push("Term count must be at least 1.");
  }
  if (membershipTypeForm.campaignEnabled && !positiveInteger(membershipTypeForm.campaignMaxSignups)) {
    planSaveIssues.push("Signup limit is required when limited presale is enabled.");
  }
  if (!nonNegativeNumber(membershipTypeForm.guestPasses)) {
    planSaveIssues.push("Guest passes must be zero or higher.");
  }
  if (membershipTypeForm.laneReservationAllowed) {
    if (!nonNegativeNumber(membershipTypeForm.advanceBookingDays)) {
      planSaveIssues.push("Advance booking days must be zero or higher.");
    }
    if (!nonNegativeNumber(membershipTypeForm.includedHoursPerMonth)) {
      planSaveIssues.push("Included lane hours must be zero or higher.");
    }
    if (!nonNegativeNumber(membershipTypeForm.maxActiveReservations)) {
      planSaveIssues.push("Max active reservations must be zero or higher.");
    }
  }
  if (membershipTypeForm.gunRentalAllowed && !nonNegativeNumber(membershipTypeForm.gunRentalDiscountPercent)) {
    planSaveIssues.push("Rental discount must be zero or higher.");
  }
  if (paidPublicPlanBlocked) {
    planSaveIssues.push("Stripe must be ready before publishing a paid public plan.");
  }
  const planSaveDisabled = savingMembershipType || planSaveIssues.length > 0;
  const planCampaignEditorOpen = Boolean(membershipTypeForm.campaignEnabled);
  const planBenefitsEditorOpen = Boolean(
    Number.parseInt(membershipTypeForm.guestPasses || "0", 10) > 0 ||
    membershipTypeForm.guestPassRollover ||
    membershipTypeForm.laneReservationAllowed ||
    membershipTypeForm.membersOnlyEventsAllowed ||
    membershipTypeForm.gunRentalAllowed
  );
  const applicationForm = normalizeApplicationFormForClient(membershipConfigForm.applicationForm);
  const applicationQuestionCount = applicationFormQuestionCount(applicationForm);
  const applicationPublished = applicationFormIsPublished(applicationForm);
  const applicationFormIssues = [];
  if (!String(applicationForm.title || "").trim()) {
    applicationFormIssues.push("Add a form title.");
  }
  applicationForm.sections.forEach((section, sectionIndex) => {
    if (!String(section.title || "").trim()) {
      applicationFormIssues.push(`Name section ${sectionIndex + 1}.`);
    }
    if (!Array.isArray(section.questions) || section.questions.length === 0) {
      applicationFormIssues.push(`Add at least one question to section ${sectionIndex + 1}.`);
    }
    (section.questions || []).forEach((question, questionIndex) => {
      if (!String(question.label || "").trim()) {
        applicationFormIssues.push(`Add text for question ${questionIndex + 1} in section ${sectionIndex + 1}.`);
      }
      if (["single_choice", "multi_choice"].includes(question.type) && (!Array.isArray(question.options) || question.options.length === 0)) {
        applicationFormIssues.push(`Add choices for "${question.label || `question ${questionIndex + 1}`}".`);
      }
      if (question.type === "multi_choice" && Number.parseInt(question.maxSelections || 0, 10) > 0 && Number.parseInt(question.maxSelections || 0, 10) > (question.options || []).length) {
        applicationFormIssues.push(`Reduce the max selections for "${question.label || `question ${questionIndex + 1}`}".`);
      }
      if (question.conditionalDetails?.enabled && !String(question.conditionalDetails.label || "").trim()) {
        applicationFormIssues.push(`Add the follow-up prompt for "${question.label || `question ${questionIndex + 1}`}".`);
      }
    });
  });
  const applicationPublishDisabled = savingMembershipConfig || applicationQuestionCount === 0 || applicationFormIssues.length > 0;
  const membershipSections = [
    { id: "plans", label: "Plans", meta: `${membershipTypes.length} types` },
    { id: "members", label: "Members", meta: `${activeMembers.length} records` },
    { id: "reviews", label: "Reviews", meta: `${pendingMembers.length + pendingQualificationRequests.length} pending` },
    { id: "application", label: "Application Form", meta: applicationPublished ? "Published" : `${applicationQuestionCount} questions` },
    { id: "requirements", label: "Requirements", meta: "Waiver + onboarding" }
  ];
  const publicSignupTypes = membershipTypes.filter((type) => type.publicSignupEnabled);
  const lifetimeTypes = membershipTypes.filter((type) => type.term?.unit === "lifetime");
  const suspendedMembers = activeMembers.filter((member) => member.status === "suspended");
  const expiringMembers = activeMembers.filter((member) => member.expiration?.expiringSoon);
  const readyApplicants = applicantMembers.filter(applicantReady);
  const qualificationCatalogCount = summary?.config?.settings?.qualificationCatalog?.length || 0;
  const onboardingChecklistItems = checklistItemsFromText(membershipConfigForm.prequalificationChecklistText);
  const qualificationCatalogItems = qualificationItemsFromText(membershipConfigForm.qualificationCatalogText);
  const assignableResources = resources
    .filter((resource) => resource.status !== "archived")
    .sort((left, right) => String(left.name || "").localeCompare(String(right.name || "")));
  const filteredAssignableResources = assignableResources.filter((resource) => {
    const query = accessResourceFilter.trim().toLowerCase();
    const typeMatch = !accessResourceTypeFilter || resource.type === accessResourceTypeFilter;
    if (!typeMatch) {
      return false;
    }
    if (!query) {
      return true;
    }
    return [
      resource.name,
      resource.type,
      resource.publicationStatus,
      resource.availabilityPolicy?.publicationStatus
    ].some((value) => String(value || "").toLowerCase().includes(query));
  });
  const assignableResourceTypes = Array.from(new Set(assignableResources.map((resource) => resource.type).filter(Boolean))).sort();
  const membershipSectionContext = {
    plans: {
      eyebrow: "Plan catalog",
      title: "Membership plans and publish readiness",
      description: "Set the plans owners can assign and members can buy. Keep pricing, terms, lifetime access, and public signup status in one place.",
      metrics: [
        { label: "Plan types", value: membershipTypes.length },
        { label: "Public signup", value: publicSignupTypes.length },
        { label: "Lifetime plans", value: lifetimeTypes.length },
        { label: "Payment readiness", value: stripeStatusLabel }
      ]
    },
    members: {
      eyebrow: "Member roster",
      title: "Member records and access status",
      description: "Add members, import a roster, update plan/status, and open member details without mixing applicant review work into the roster.",
      metrics: [
        { label: "Roster records", value: activeMembers.length },
        { label: "Active access", value: activeAccessMembers.length },
        { label: "Suspended", value: suspendedMembers.length },
        { label: "Expiring soon", value: expiringMembers.length }
      ]
    },
    reviews: {
      eyebrow: "Review queue",
      title: "Applicant and qualification reviews",
      description: "Work through paid public signups, waiver status, qualification checks, and final approval decisions.",
      metrics: [
        { label: "Applicants", value: applicantMembers.length },
        { label: "Pending applicants", value: pendingMembers.length },
        { label: "Ready to approve", value: readyApplicants.length },
        { label: "Qualification requests", value: pendingQualificationRequests.length }
      ]
    },
    application: {
      eyebrow: "Applicant form",
      title: "Membership application form",
      description: "Build the questions new members answer before owner review. Publish only when the form is ready for applicants.",
      metrics: [
        { label: "Status", value: applicationPublished ? "Published" : applicationQuestionCount > 0 ? "Draft" : "Not built" },
        { label: "Sections", value: applicationForm.sections.length },
        { label: "Questions", value: applicationQuestionCount },
        { label: "Version", value: applicationForm.version || 1 }
      ]
    },
    requirements: {
      eyebrow: "Signup rules",
      title: "Waiver and onboarding requirements",
      description: "Define what a new member must sign or complete before they become active.",
      metrics: [
        { label: "Waiver", value: membershipConfigForm.waiverRequired ? "Required" : "Optional" },
        { label: "Waiver expiry", value: Number.parseInt(membershipConfigForm.waiverExpiresInDays || "0", 10) > 0 ? `${membershipConfigForm.waiverExpiresInDays} days` : "No expiry" },
        { label: "Onboarding", value: membershipConfigForm.prequalificationRequired ? "Required" : "Optional" },
        { label: "Qualification keys", value: qualificationCatalogCount }
      ]
    }
  };
  const activeMembershipContext = membershipSectionContext[activeMembershipSection] || membershipSectionContext.plans;

  function membershipTypeName(membershipTypeId, fallback = "Plan needs review") {
    if (!membershipTypeId) {
      return "No plan";
    }
    const type = membershipTypes.find((item) => item.membershipTypeId === membershipTypeId);
    return type?.name || fallback;
  }

  function switchMembershipSection(sectionId) {
    setActiveMembershipSection(sectionId);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#membership-${sectionId}`);
    }
  }

  function nextApplicationForm(updater) {
    const current = normalizeApplicationFormForClient(membershipConfigForm.applicationForm);
    return typeof updater === "function" ? updater(current) : updater;
  }

  function setApplicationForm(updater) {
    setMembershipConfigForm((current) => ({
      ...current,
      applicationForm: nextApplicationForm(updater)
    }));
  }

  function saveApplicationForm(form, publish = false) {
    const normalized = normalizeApplicationFormForClient(form);
    const nextForm = {
      ...normalized,
      enabled: publish ? true : normalized.enabled,
      status: publish ? "published" : normalized.status === "not_configured" ? "draft" : normalized.status,
      version: publish ? Number.parseInt(normalized.version || 1, 10) + (normalized.status === "published" ? 1 : 0) : normalized.version
    };
    const nextConfig = {
      ...membershipConfigForm,
      applicationForm: nextForm,
      prequalificationRequired: publish ? true : membershipConfigForm.prequalificationRequired
    };
    setMembershipConfigForm(nextConfig);
    onSaveMembershipConfig(null, nextConfig);
  }

  function loadFirearmsScreeningTemplate() {
    setApplicationForm({
      ...FIREARMS_SCREENING_TEMPLATE,
      version: Math.max(1, Number.parseInt(applicationForm.version || 1, 10))
    });
  }

  function updateApplicationFormField(field, value) {
    setApplicationForm((form) => ({ ...form, [field]: value, status: form.status === "published" ? "draft" : form.status }));
  }

  function addApplicationSection() {
    setApplicationForm((form) => ({
      ...form,
      status: form.status === "published" ? "draft" : "draft",
      sections: [
        ...(form.sections || []),
        {
          sectionId: `section_${Date.now()}`,
          title: "New section",
          description: "",
          questions: []
        }
      ]
    }));
  }

  function updateApplicationSection(sectionIndex, patch) {
    setApplicationForm((form) => ({
      ...form,
      status: form.status === "published" ? "draft" : form.status,
      sections: form.sections.map((section, index) => index === sectionIndex
        ? {
            ...section,
            ...patch,
            sectionId: patch.title && (!section.sectionId || /^section_\d+$/.test(section.sectionId))
              ? slugifyFormKey(patch.title) || section.sectionId
              : section.sectionId
          }
        : section)
    }));
  }

  function removeApplicationSection(sectionIndex) {
    setApplicationForm((form) => ({
      ...form,
      status: "draft",
      sections: form.sections.filter((_, index) => index !== sectionIndex)
    }));
  }

  function addApplicationQuestion(sectionIndex) {
    setApplicationForm((form) => ({
      ...form,
      status: "draft",
      sections: form.sections.map((section, index) => index === sectionIndex
        ? {
            ...section,
            questions: [
              ...(section.questions || []),
              {
                questionId: `question_${Date.now()}`,
                label: "New question",
                type: "short_text",
                required: true,
                helpText: "",
                options: [],
                maxSelections: 0,
                allowOther: false
              }
            ]
          }
        : section)
    }));
  }

  function updateApplicationQuestion(sectionIndex, questionIndex, patch) {
    setApplicationForm((form) => ({
      ...form,
      status: "draft",
      sections: form.sections.map((section, sIndex) => sIndex === sectionIndex
        ? {
            ...section,
            questions: section.questions.map((question, qIndex) => qIndex === questionIndex
              ? {
                  ...question,
                  ...patch,
                  questionId: patch.label && (!question.questionId || /^question_\d+$/.test(question.questionId))
                    ? slugifyFormKey(patch.label) || question.questionId
                    : question.questionId,
                  options: patch.type && !["single_choice", "multi_choice"].includes(patch.type)
                    ? []
                    : patch.options || question.options,
                  maxSelections: patch.type && patch.type !== "multi_choice" ? 0 : patch.maxSelections ?? question.maxSelections,
                  allowOther: patch.type && patch.type !== "multi_choice" ? false : patch.allowOther ?? question.allowOther
                }
              : question)
          }
        : section)
    }));
  }

  function updateApplicationQuestionOptions(sectionIndex, questionIndex, value) {
    const options = String(value || "")
      .split(/\r?\n/)
      .map((label) => label.trim())
      .filter(Boolean)
      .slice(0, 40)
      .map((label) => ({ optionId: slugifyFormKey(label), label }));
    updateApplicationQuestion(sectionIndex, questionIndex, { options });
  }

  function removeApplicationQuestion(sectionIndex, questionIndex) {
    setApplicationForm((form) => ({
      ...form,
      status: "draft",
      sections: form.sections.map((section, sIndex) => sIndex === sectionIndex
        ? { ...section, questions: section.questions.filter((_, qIndex) => qIndex !== questionIndex) }
        : section)
    }));
  }

  function requirementStatus(member, key) {
    return member.review?.requirements?.[key]?.status || "pending";
  }

  function requirementPolicyRequired(key) {
    if (key === "waiver") {
      return Boolean(membershipConfigForm.waiverRequired);
    }
    if (key === "prequalification") {
      return Boolean(membershipConfigForm.prequalificationRequired);
    }
    if (key === "backgroundCheck") {
      return Boolean(membershipConfigForm.backgroundCheckRequired);
    }
    return true;
  }

  function requirementRequired(member, key) {
    return requirementPolicyRequired(key) && member.review?.requirements?.[key]?.required !== false;
  }

  function buildRequirementPatch(member, key, status) {
    const currentRequirements = member.review?.requirements || {};
    return {
      reviewRequirements: {
        ...currentRequirements,
        [key]: {
          ...(currentRequirements[key] || {}),
          required: requirementRequired(member, key),
          status
        }
      }
    };
  }

  function buildPrequalificationDecisionPatch(status, draft) {
    return {
      prequalificationDecision: {
        status,
        reason: draft.reason.trim() || undefined
      },
      notes: draft.notes.trim() || undefined
    };
  }

  function buildRequestInfoPatch(draft) {
    const reason = draft.reason.trim() || "Additional information requested by range review.";
    return {
      review: {
        status: "needs_info",
        reason
      },
      reason,
      notes: draft.notes.trim() || undefined
    };
  }

  function buildBackgroundCheckDecisionPatch(status, draft, extra = {}) {
    const reason = draft.reason.trim()
      || (status === "failed" ? "Background check was not approved. Prescreening fee is non-refundable." : undefined);
    return {
      ...extra,
      backgroundCheckDecision: {
        status,
        reason
      },
      reason,
      notes: draft.notes.trim() || undefined
    };
  }

  function buildChecklistStepPatch(member, requirementKey, stepKey) {
    const currentRequirements = member.review?.requirements || {};
    const requirement = currentRequirements[requirementKey] || {};
    const checklist = (requirement.checklist || []).map((step) => (
      step.key === stepKey
        ? { ...step, status: "completed", completedAt: new Date().toISOString() }
        : step
    ));
    const complete = checklist.every((step) => !step.required || step.status === "completed");

    return {
      reviewRequirements: {
        ...currentRequirements,
        [requirementKey]: {
          ...requirement,
          checklist,
          status: complete ? "completed" : "pending"
        }
      }
    };
  }

  function reviewDraft(memberId) {
    return reviewDrafts[memberId] || { notes: "", reason: "" };
  }

  function updateReviewDraft(memberId, patch) {
    setReviewDrafts((current) => ({
      ...current,
      [memberId]: {
        ...reviewDraft(memberId),
        ...patch
      }
    }));
  }

  function renewalDraft(memberId) {
    return renewalDrafts[memberId] || { startsAt: "", endsAt: "", reason: "" };
  }

  function updateRenewalDraft(memberId, patch) {
    setRenewalDrafts((current) => ({
      ...current,
      [memberId]: {
        ...renewalDraft(memberId),
        ...patch
      }
    }));
  }

  function planChangeDraft(memberId) {
    return planChangeDrafts[memberId] || {
      membershipTypeId: "",
      effectiveAt: "",
      resetTerm: true,
      grantGuestPasses: true,
      reason: ""
    };
  }

  function updatePlanChangeDraft(memberId, patch) {
    setPlanChangeDrafts((current) => ({
      ...current,
      [memberId]: {
        ...planChangeDraft(memberId),
        ...patch
      }
    }));
  }

  function guestPassDraft(memberId) {
    return guestPassDrafts[memberId] || { action: "use", quantity: "1", reason: "" };
  }

  function updateGuestPassDraft(memberId, patch) {
    setGuestPassDrafts((current) => ({
      ...current,
      [memberId]: {
        ...guestPassDraft(memberId),
        ...patch
      }
    }));
  }

  function statusDraft(memberId) {
    return statusDrafts[memberId] || { status: "", reason: "", suspensionStartsAt: "", suspensionEndsAt: "" };
  }

  function profileDraft(member) {
    const memberId = member?.memberId || "";
    return profileDrafts[memberId] || {
      fullName: member?.fullName || "",
      email: member?.email || "",
      phone: member?.phone || "",
      notes: member?.notes || "",
      accessCredentialStatus: member?.accessCredential?.status || "not_issued",
      accessCredentialLabel: member?.accessCredential?.label || "",
      accessCredentialCardId: member?.accessCredential?.cardId || "",
      accessCredentialExpiresAt: member?.accessCredential?.expiresAt ? toDatetimeLocal(member.accessCredential.expiresAt) : "",
      accessCredentialNotes: member?.accessCredential?.notes || ""
    };
  }

  function updateProfileDraft(member, patch) {
    const memberId = member?.memberId || "";
    setProfileDrafts((current) => ({
      ...current,
      [memberId]: {
        ...profileDraft(member),
        ...patch
      }
    }));
  }

  function resetProfileDraft(member) {
    setProfileDrafts((current) => {
      const next = { ...current };
      delete next[member.memberId];
      return next;
    });
  }

  function quickStatusChange(member, status, defaultReason) {
    const reason = window.prompt(`${status === "archived" ? "Archive" : status === "suspended" ? "Suspend" : "Reactivate"} reason`, defaultReason);
    if (!reason?.trim()) {
      return;
    }
    onUpdateMemberStatus(member.memberId, {
      status,
      reason: reason.trim(),
      source: "owner_quick_action"
    });
  }

  function updateStatusDraft(memberId, patch) {
    setStatusDrafts((current) => ({
      ...current,
      [memberId]: {
        ...statusDraft(memberId),
        ...patch
      }
    }));
  }

  function householdDraft(memberId) {
    return householdDrafts[memberId] || {
      fullName: "",
      email: "",
      phone: "",
      emergencyContactName: "",
      emergencyContactPhone: "",
      relationship: "family",
      status: "active",
      waiverStatus: "pending",
      notes: ""
    };
  }

  function updateHouseholdDraft(memberId, patch) {
    setHouseholdDrafts((current) => ({
      ...current,
      [memberId]: {
        ...householdDraft(memberId),
        ...patch
      }
    }));
  }

  function flagDraft(memberId) {
    return flagDrafts[memberId] || {
      type: "safety",
      severity: "warning",
      title: "",
      notes: ""
    };
  }

  function documentDraft(memberId) {
    return documentDrafts[memberId] || {
      type: "waiver",
      status: "received",
      title: "",
      url: "",
      notes: ""
    };
  }

  function updateDocumentDraft(memberId, patch) {
    setDocumentDrafts((current) => ({
      ...current,
      [memberId]: {
        ...documentDraft(memberId),
        ...patch
      }
    }));
  }

  function updateFlagDraft(memberId, patch) {
    setFlagDrafts((current) => ({
      ...current,
      [memberId]: {
        ...flagDraft(memberId),
        ...patch
      }
    }));
  }

  function qualificationDraft(memberId) {
    return qualificationDrafts[memberId] || {
      catalogKey: "",
      key: "",
      label: "",
      status: "active",
      issuedAt: "",
      expiresAt: "",
      notes: ""
    };
  }

  function qualificationStatusLabel(qualification) {
    const effectiveStatus = qualification.effectiveStatus || qualification.status;
    if (effectiveStatus === "expired" && qualification.status !== "expired") {
      return "expired by date";
    }
    return effectiveStatus;
  }

  function updateQualificationDraft(memberId, patch) {
    setQualificationDrafts((current) => ({
      ...current,
      [memberId]: {
        ...qualificationDraft(memberId),
        ...patch
      }
    }));
  }

  function applyCatalogQualification(memberId, catalogKey) {
    const qualificationCatalog = summary.config?.settings?.qualificationCatalog || [];
    const item = qualificationCatalog.find((qualification) => qualification.key === catalogKey);
    if (!item) {
      updateQualificationDraft(memberId, { catalogKey: "", key: "", label: "", expiresAt: "" });
      return;
    }

    updateQualificationDraft(memberId, {
      catalogKey: item.key,
      key: item.key,
      label: item.label,
      expiresAt: datetimeLocalDaysFromNow(item.defaultExpiresInDays)
    });
  }

  function requirementOpen(member, key) {
    const requirement = member.review?.requirements?.[key] || {};
    return requirementPolicyRequired(key) && requirement.required !== false && requirement.status !== "completed";
  }

  function checklistOpen(member, key) {
    const requirements = key
      ? [[key, member.review?.requirements?.[key] || {}]]
      : [
          ["waiver", member.review?.requirements?.waiver || {}],
          ["prequalification", member.review?.requirements?.prequalification || {}],
          ["backgroundCheck", member.review?.requirements?.backgroundCheck || {}]
        ];
    return requirements.some((requirement) => (
      requirementPolicyRequired(requirement[0])
      && ((requirement[1].checklist || []).some((step) => step.required !== false && step.status !== "completed"))
    ));
  }

  function applicantReady(member) {
    if (member.planChangeRequest?.status === "pending") {
      return true;
    }
    return (
      member.status === "pending" &&
      !requirementOpen(member, "waiver") &&
      !requirementOpen(member, "prequalification") &&
      !requirementOpen(member, "backgroundCheck") &&
      !checklistOpen(member)
    );
  }

  function updateChecklistItems(fieldName, patcher) {
    setMembershipConfigForm((current) => {
      const nextItems = patcher(checklistItemsFromText(current[fieldName]));
      return {
        ...current,
        [fieldName]: checklistItemsToText(nextItems)
      };
    });
  }

  function removeChecklistItem(fieldName, indexToRemove) {
    updateChecklistItems(fieldName, (items) => items.filter((_, index) => index !== indexToRemove));
  }

  function updateQualificationItems(patcher) {
    setMembershipConfigForm((current) => {
      const nextItems = patcher(qualificationItemsFromText(current.qualificationCatalogText));
      return {
        ...current,
        qualificationCatalogText: qualificationItemsToText(nextItems)
      };
    });
  }

  function removeQualificationItem(indexToRemove) {
    updateQualificationItems((items) => items.filter((_, index) => index !== indexToRemove));
  }

  function resourceQualificationKeys(resource = {}) {
    return Array.isArray(resource.accessPolicy?.requiredQualificationKeys)
      ? resource.accessPolicy.requiredQualificationKeys.map(normalizeQualificationKey).filter(Boolean)
      : [];
  }

  function qualificationAppliedResources(qualification) {
    const key = normalizeQualificationKey(qualification.key || slugifyRequirementKey(qualification.label));
    return assignableResources.filter((resource) => resourceQualificationKeys(resource).includes(key));
  }

  function toggleResourceQualification(resource, qualification, checked) {
    if (!onUpdateResource) {
      return;
    }
    const key = normalizeQualificationKey(qualification.key || slugifyRequirementKey(qualification.label));
    if (!key) {
      return;
    }
    const currentKeys = resourceQualificationKeys(resource);
    const nextKeys = checked
      ? Array.from(new Set([...currentKeys, key]))
      : currentKeys.filter((item) => item !== key);
    onUpdateResource(resource.resourceId, {
      accessPolicy: {
        ...(resource.accessPolicy || {}),
        requiredQualificationKeys: nextKeys
      }
    });
  }

  function openRequirementBuilder(type) {
    setRequirementGateDraft({
      type,
      label: "",
      key: "",
      expiresInDays: "365",
      note: ""
    });
    setRequirementsAddOpen(true);
  }

  function handleAddRequirementGate(event) {
    event.preventDefault();
    const label = requirementGateDraft.label.trim();
    if (!label) {
      return;
    }

    const key = slugifyRequirementKey(requirementGateDraft.key || label);
    setMembershipConfigForm((current) => {
      if (requirementGateDraft.type === "qualification") {
        const items = qualificationItemsFromText(current.qualificationCatalogText);
        return {
          ...current,
          qualificationCatalogText: qualificationItemsToText([
            ...items,
            {
              label,
              key: key || slugifyRequirementKey(label),
              defaultExpiresInDays: Number.parseInt(requirementGateDraft.expiresInDays || "0", 10) > 0
                ? requirementGateDraft.expiresInDays
                : "",
              description: requirementGateDraft.note.trim()
            }
          ])
        };
      }

      if (requirementGateDraft.type === "background") {
        const items = checklistItemsFromText(current.backgroundCheckChecklistText);
        return {
          ...current,
          backgroundCheckRequired: true,
          backgroundCheckChecklistText: checklistItemsToText([
            ...items,
            { label, instructions: requirementGateDraft.note.trim() }
          ])
        };
      }

      const items = checklistItemsFromText(current.prequalificationChecklistText);
      return {
        ...current,
        prequalificationRequired: true,
        prequalificationChecklistText: checklistItemsToText([
          ...items,
          { label, instructions: requirementGateDraft.note.trim() }
        ])
      };
    });

    setRequirementGateDraft({
      type: requirementsSubsection === "access" ? "qualification" : "onboarding",
      label: "",
      key: "",
      expiresInDays: "365",
      note: ""
    });
    setRequirementsAddOpen(false);
  }

  function exportApplicantsCsv() {
    const rows = [
      [
        "Name",
        "Email",
        "Status",
        "Review status",
        "Payment status",
        "Subscription status",
        "Membership type",
        "Waiver",
        "Waiver acknowledged at",
        "Qualification",
        "Qualification submitted at",
        "Qualification steps completed",
        "Qualification steps total",
        "Qualification responses",
        "Requirements complete",
        "Source",
        "Campaign",
        "Signup ID",
        "Updated"
      ],
      ...filteredApplicants.map((member) => {
        const summary = member.reviewSummary || {};
        return [
          member.fullName,
          member.email,
          member.status,
          summary.reviewStatus || member.review?.status || "pending",
          member.stripe?.paymentStatus || "",
          member.stripe?.subscriptionStatus || "",
          membershipTypeName(member.membershipTypeId),
          summary.waiverStatus || requirementStatus(member, "waiver"),
          summary.applicantWaiverAcknowledgedAt || "",
          summary.prequalificationStatus || requirementStatus(member, "prequalification"),
          summary.applicantPrequalificationSubmittedAt || "",
          summary.prequalificationStepsCompleted ?? "",
          summary.prequalificationStepsTotal ?? "",
          prequalificationResponseSummary(member),
          summary.requirementsComplete === undefined ? "" : String(Boolean(summary.requirementsComplete)),
          member.source || "",
          member.campaign?.name || member.campaign?.code || "",
          member.signupIntentId,
          member.updatedAt || member.createdAt
        ];
      })
    ];
    const csv = rows.map((row) => row.map(csvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rangegrid-applicants-${selectedRange.slug || selectedRange.rangeId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function downloadReceipt(receipt) {
    downloadReceiptDocument(receipt, {
      range: selectedRange,
      title: "Member receipt"
    });
  }

  function renderMemberDetail() {
    const member = memberDetail?.member;
    const payment = memberDetail?.payment || {};
    const profileDraftValue = member ? profileDraft(member) : { fullName: "", email: "", phone: "", notes: "" };
    const renewalDraftValue = member ? renewalDraft(member.memberId) : { startsAt: "", endsAt: "", reason: "" };
    const planChangeDraftValue = member ? planChangeDraft(member.memberId) : {
      membershipTypeId: "",
      effectiveAt: "",
      resetTerm: true,
      grantGuestPasses: true,
      reason: ""
    };
    const guestPassLedger = member?.guestPassLedger || {};
    const guestPassDraftValue = member ? guestPassDraft(member.memberId) : { action: "use", quantity: "1", reason: "" };
    const statusDraftValue = member ? statusDraft(member.memberId) : { status: "", reason: "" };
    const householdDraftValue = member ? householdDraft(member.memberId) : {
      fullName: "",
      email: "",
      phone: "",
      emergencyContactName: "",
      emergencyContactPhone: "",
      relationship: "family",
      status: "active",
      waiverStatus: "pending",
      notes: ""
    };
    const flagDraftValue = member ? flagDraft(member.memberId) : {
      type: "safety",
      severity: "warning",
      title: "",
      notes: ""
    };
    const documentDraftValue = member ? documentDraft(member.memberId) : {
      type: "waiver",
      status: "received",
      title: "",
      url: "",
      notes: ""
    };
    const qualificationDraftValue = member ? qualificationDraft(member.memberId) : {
      catalogKey: "",
      key: "",
      label: "",
      status: "active",
      issuedAt: "",
      expiresAt: "",
      notes: ""
    };
    const filteredFlags = (member?.flags?.items || []).filter((flag) => (
      (!flagSeverityFilter || flag.severity === flagSeverityFilter) &&
      (!flagStatusFilter || flag.status === flagStatusFilter)
    ));
    const relatedNotifications = memberDetail?.notifications || [];
    const visibleRelatedNotifications = memberActivityExpanded.notifications
      ? relatedNotifications
      : relatedNotifications.slice(0, 6);
    const timelineItems = memberTimeline?.timeline || [];
    const visibleTimelineItems = memberActivityExpanded.timeline
      ? timelineItems
      : timelineItems.slice(0, 8);

    if (!selectedMemberDetailId) {
      return null;
    }

    return (
      <section className={`member-detail-panel member-detail-mode-${memberDetailMode}`}>
        <div className="panel-header">
          <div>
            <h3>Member detail</h3>
            <p>Review profile, access status, visits, and account history.</p>
          </div>
          <button type="button" className="secondary-button" onClick={onCloseMemberDetail}>
            Close
          </button>
        </div>

        {memberDetailLoading && <div className="empty-state">Loading member detail...</div>}
        {!memberDetailLoading && member && (
          <>
            <div className="member-detail-mode-tabs" role="tablist" aria-label="Member detail views">
              {[
                { id: "overview", label: "Overview", description: "Snapshot and history" },
                { id: "edit", label: "Edit profile", description: "Name, contact, notes" },
                { id: "operations", label: "Operations", description: "Plan, status, documents" }
              ].map((mode) => (
                <button
                  type="button"
                  className={memberDetailMode === mode.id ? "is-active" : ""}
                  key={mode.id}
                  role="tab"
                  aria-selected={memberDetailMode === mode.id}
                  onClick={() => setMemberDetailMode(mode.id)}
                >
                  <strong>{mode.label}</strong>
                  <span>{mode.description}</span>
                </button>
              ))}
            </div>

            <div className="member-operations-summary" aria-label="Member operations summary">
              <div>
                <span>Access status</span>
                <strong>{member.status}</strong>
                <small>{member.suspension ? "Suspension window active" : "Use status operations for access changes"}</small>
              </div>
              <div>
                <span>Compliance</span>
                <strong>{member.documents?.items?.length || 0} documents</strong>
                <small>{member.qualifications?.activeCount || 0} active qualifications</small>
              </div>
              <div>
                <span>Access credential</span>
                <strong>{accessCredentialStatusLabel(member.accessCredential?.status)}</strong>
                <small>{member.accessCredential?.cardLast4 ? `Card ending ${member.accessCredential.cardLast4}` : "RFID/access card readiness"}</small>
              </div>
              <div>
                <span>Plan control</span>
                <strong>{memberDetail.membershipType?.name || "Plan unavailable"}</strong>
                <small>{payment.subscriptionId ? "Stripe follow-up may be required" : formatExpiration(member)}</small>
              </div>
              <div>
                <span>Household and guests</span>
                <strong>{member.household?.count || 0} household / {guestPassLedger.remaining || 0} passes</strong>
                <small>Manage guests and linked household access</small>
              </div>
            </div>

            <div className="member-overview-panel">
              <div className="member-overview-profile">
                <div className="member-overview-identity">
                  <span>Selected member</span>
                  <h4>{member.fullName}</h4>
                  <p>
                    {member.email || "No email"}
                    {member.phone ? ` / ${member.phone}` : ""}
                  </p>
                </div>
                <div className="member-overview-current-status">
                  <span>Current access</span>
                  <strong>{member.status}</strong>
                  <small>{formatExpiration(member)}</small>
                </div>
              </div>

              <dl className="member-overview-list">
                <div>
                  <dt>Plan</dt>
                  <dd>
                    <strong>{memberDetail.membershipType?.name || "Plan unavailable"}</strong>
                    <small>{memberDetail.membershipType ? formatMembershipBilling(memberDetail.membershipType) : membershipTypeName(member.membershipTypeId)}</small>
                  </dd>
                </div>
                <div>
                  <dt>Review readiness</dt>
                  <dd>
                    <strong>{member.review?.status || "pending"}</strong>
                    <small>{memberDetail.summary?.reviewReady ? "Requirements complete" : "Requirements pending"}</small>
                  </dd>
                </div>
                <div>
                  <dt>Membership term</dt>
                  <dd>
                    <strong>{member.expiration?.effectiveStatus || member.status}</strong>
                    <small>{formatExpiration(member)}</small>
                  </dd>
                </div>
                <div>
                  <dt>Payment record</dt>
                  <dd>
                    <strong>{compactValue(payment.paymentStatus || payment.subscriptionStatus)}</strong>
                    <small>{payment.subscriptionId ? "Stripe subscription linked" : payment.checkoutSessionId ? "Stripe checkout linked" : "No Stripe record"}</small>
                  </dd>
                </div>
                <div>
                  <dt>Compliance</dt>
                  <dd>
                    <strong>{member.documents?.items?.length || 0} documents</strong>
                    <small>{member.qualifications?.activeCount || 0} active qualifications</small>
                  </dd>
                </div>
                <div>
                  <dt>Household and guests</dt>
                  <dd>
                    <strong>{member.household?.count || 0} household / {guestPassLedger.remaining || 0} guest passes</strong>
                    <small>
                      {guestPassLedger.used || 0} passes used
                      {guestPassLedger.nextExpirationAt ? ` / next expires ${formatDateTime(guestPassLedger.nextExpirationAt)}` : ""}
                    </small>
                  </dd>
                </div>
                <div>
                  <dt>Internal flags</dt>
                  <dd>
                    <strong>{member.flags?.activeCount || 0} active</strong>
                    <small>{member.flags?.criticalCount || 0} critical</small>
                  </dd>
                </div>
                <div>
                  <dt>Qualification status</dt>
                  <dd>
                    <strong>{member.qualifications?.activeCount || 0} active</strong>
                    <small>
                      {(member.qualifications?.pendingCount || 0)} pending
                      {" / "}
                      {(member.qualifications?.expiredCount || 0)} expired
                    </small>
                  </dd>
                </div>
                <div>
                  <dt>Access credential</dt>
                  <dd>
                    <strong>{accessCredentialStatusLabel(member.accessCredential?.status)}</strong>
                    <small>
                      {member.accessCredential?.cardLast4
                        ? `RFID/access card ending ${member.accessCredential.cardLast4}`
                        : "No physical access card reference recorded"}
                    </small>
                  </dd>
                </div>
              </dl>
            </div>

            <form
              className="member-detail-section member-renewal-form member-profile-form"
              onSubmit={(event) => {
                event.preventDefault();
                onUpdateMemberProfile(member.memberId, {
                  fullName: profileDraftValue.fullName,
                  email: profileDraftValue.email,
                  phone: profileDraftValue.phone,
                  notes: profileDraftValue.notes,
                  accessCredential: {
                    status: profileDraftValue.accessCredentialStatus,
                    label: profileDraftValue.accessCredentialLabel,
                    cardId: profileDraftValue.accessCredentialCardId,
                    expiresAt: profileDraftValue.accessCredentialExpiresAt,
                    notes: profileDraftValue.accessCredentialNotes
                  }
                });
              }}
            >
              <h4>Member profile</h4>
              <div className="form-row">
                <label>
                  Full name
                  <input
                    value={profileDraftValue.fullName}
                    onChange={(event) => updateProfileDraft(member, { fullName: event.target.value })}
                    required
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={profileDraftValue.email}
                    onChange={(event) => updateProfileDraft(member, { email: event.target.value })}
                    placeholder="member@example.com"
                  />
                </label>
              </div>
              <label>
                Phone
                <input
                  value={profileDraftValue.phone}
                  onChange={(event) => updateProfileDraft(member, { phone: event.target.value })}
                  placeholder="Optional"
                />
              </label>
              <label>
                Notes
                <textarea
                  value={profileDraftValue.notes}
                  onChange={(event) => updateProfileDraft(member, { notes: event.target.value })}
                  placeholder="Internal member notes"
                  rows="3"
                />
              </label>
              <div className="member-access-credential-editor">
                <div>
                  <h4>RFID / access card</h4>
                  <p>Track whether the member is ready for card-controlled areas such as a separate 1,000-yard gate.</p>
                </div>
                <div className="form-row">
                  <label>
                    Credential status
                    <select
                      value={profileDraftValue.accessCredentialStatus}
                      onChange={(event) => updateProfileDraft(member, { accessCredentialStatus: event.target.value })}
                    >
                      <option value="not_issued">Not issued</option>
                      <option value="ready_to_issue">Ready to issue</option>
                      <option value="issued">Issued</option>
                      <option value="active">Active</option>
                      <option value="disabled">Disabled</option>
                      <option value="lost">Lost</option>
                      <option value="returned">Returned</option>
                    </select>
                  </label>
                  <label>
                    Card label
                    <input
                      value={profileDraftValue.accessCredentialLabel}
                      onChange={(event) => updateProfileDraft(member, { accessCredentialLabel: event.target.value })}
                      placeholder="RFID card, gate fob, clubhouse badge"
                    />
                  </label>
                  <label>
                    Card ID or reference
                    <input
                      value={profileDraftValue.accessCredentialCardId}
                      onChange={(event) => updateProfileDraft(member, { accessCredentialCardId: event.target.value })}
                      placeholder="Optional staff-only reference"
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Expires
                    <input
                      type="datetime-local"
                      value={profileDraftValue.accessCredentialExpiresAt}
                      onChange={(event) => updateProfileDraft(member, { accessCredentialExpiresAt: event.target.value })}
                    />
                  </label>
                  <label>
                    Credential notes
                    <input
                      value={profileDraftValue.accessCredentialNotes}
                      onChange={(event) => updateProfileDraft(member, { accessCredentialNotes: event.target.value })}
                      placeholder="Issued at orientation, replacement needed, gate fob disabled"
                    />
                  </label>
                </div>
              </div>
              <div className="button-row">
                <button
                  type="submit"
                  disabled={updatingMemberProfileId === member.memberId || !profileDraftValue.fullName.trim()}
                >
                  {updatingMemberProfileId === member.memberId ? "Saving..." : "Save profile"}
                </button>
                <button type="button" className="secondary-button" onClick={() => resetProfileDraft(member)}>
                  Reset
                </button>
              </div>
            </form>

            <div className="member-detail-section">
              <h4>Payment references</h4>
              <dl>
                <div>
                  <dt>Checkout</dt>
                  <dd>{payment.checkoutSessionId ? "Checkout linked" : "Not recorded"}</dd>
                </div>
                <div>
                  <dt>Subscription</dt>
                  <dd>{payment.subscriptionId ? "Subscription linked" : "Not recorded"}</dd>
                </div>
                <div>
                  <dt>Current period end</dt>
                  <dd>{formatDateTime(payment.currentPeriodEnd)}</dd>
                </div>
                <div>
                  <dt>Last lifecycle event</dt>
                  <dd>{reviewStatusCopy(payment.lastLifecycleEvent, "Not recorded")}</dd>
                </div>
              </dl>
            </div>

            <TaskDisclosure
              actionLabel="Manage"
              title="Compliance documents"
              description="Record waivers, IDs, prequalification files, and other supporting documents."
            >
            <form
              className="member-detail-section member-renewal-form"
              onSubmit={(event) => {
                event.preventDefault();
                onAddMemberDocument(member.memberId, documentDraftValue);
                updateDocumentDraft(member.memberId, {
                  type: "waiver",
                  status: "received",
                  title: "",
                  url: "",
                  notes: ""
                });
              }}
            >
              <h4>Documents</h4>
              <div className="form-row">
                <label>
                  Type
                  <select
                    value={documentDraftValue.type}
                    onChange={(event) => updateDocumentDraft(member.memberId, { type: event.target.value })}
                  >
                    <option value="waiver">Waiver</option>
                    <option value="prequalification">Prequalification</option>
                    <option value="id">ID</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label>
                  Status
                  <select
                    value={documentDraftValue.status}
                    onChange={(event) => updateDocumentDraft(member.memberId, { status: event.target.value })}
                  >
                    <option value="received">Received</option>
                    <option value="pending_review">Pending review</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                  </select>
                </label>
              </div>
              <label>
                Title
                <input
                  value={documentDraftValue.title}
                  onChange={(event) => updateDocumentDraft(member.memberId, { title: event.target.value })}
                  placeholder="Signed waiver"
                  required
                />
              </label>
              <label>
                Secure file link or internal reference
                <input
                  value={documentDraftValue.url}
                  onChange={(event) => updateDocumentDraft(member.memberId, { url: event.target.value })}
                  placeholder="Paste a secure file link or storage reference"
                />
                <small>Range staff see whether a document is linked without exposing long storage paths.</small>
              </label>
              <label>
                Notes
                <input
                  value={documentDraftValue.notes}
                  onChange={(event) => updateDocumentDraft(member.memberId, { notes: event.target.value })}
                  placeholder="Internal document context"
                />
              </label>
              <button
                type="submit"
                disabled={addingMemberDocumentId === member.memberId || !documentDraftValue.title.trim()}
              >
                {addingMemberDocumentId === member.memberId ? "Adding..." : "Add document"}
              </button>
              {member.documents?.items?.length > 0 ? (
                <div className="member-detail-list">
                  {member.documents.items.slice(0, 8).map((document) => (
                    <div key={document.documentId}>
                      <strong>{document.title}</strong>
                      <span>{documentTypeLabel(document.type)} / {reviewStatusCopy(document.status, "Needs review")}</span>
                      {document.url && <span>{documentReferenceLabel(document.url)}</span>}
                      {document.notes && <span>{document.notes}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No member documents recorded.</div>
              )}
            </form>
            </TaskDisclosure>

            <TaskDisclosure
              actionLabel="Manage"
              title="Access status"
              description="Suspend, reactivate, cancel, or archive access with a clear reason."
            >
            <form
              className="member-detail-section member-renewal-form"
              onSubmit={(event) => {
                event.preventDefault();
                onUpdateMemberStatus(member.memberId, {
                  status: statusDraftValue.status,
                  reason: statusDraftValue.reason,
                  suspensionStartsAt: statusDraftValue.status === "suspended"
                    ? statusDraftValue.suspensionStartsAt
                    : undefined,
                  suspensionEndsAt: statusDraftValue.status === "suspended"
                    ? statusDraftValue.suspensionEndsAt
                    : undefined
                });
                updateStatusDraft(member.memberId, {
                  status: "",
                  reason: "",
                  suspensionStartsAt: "",
                  suspensionEndsAt: ""
                });
              }}
            >
              <h4>Status operations</h4>
              {member.suspension && (
                <div className="detail-callout">
                  <strong>Active suspension</strong>
                  <span>{member.suspension.reason || member.statusReason || "No reason recorded"}</span>
                  <span>
                    Starts {formatDateTime(member.suspension.startsAt)}
                    {member.suspension.endsAt ? ` / Ends ${formatDateTime(member.suspension.endsAt)}` : " / No end date"}
                  </span>
                </div>
              )}
              <div className="form-row">
                <label>
                  Current status
                  <input value={member.status} disabled />
                </label>
                <label>
                  New status
                  <select
                    value={statusDraftValue.status}
                    onChange={(event) => updateStatusDraft(member.memberId, { status: event.target.value })}
                    required
                  >
                    <option value="">Choose a status</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending review</option>
                    <option value="expired">Expired</option>
                    <option value="suspended">Suspended</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="archived">Archived</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
              </div>
              <label>
                Reason
                <input
                  value={statusDraftValue.reason}
                  onChange={(event) => updateStatusDraft(member.memberId, { reason: event.target.value })}
                  placeholder="Waiver expired, payment fixed, member requested cancellation"
                  required
                />
              </label>
              {statusDraftValue.status === "suspended" && (
                <div className="form-row">
                  <label>
                    Suspension starts
                    <input
                      type="datetime-local"
                      value={statusDraftValue.suspensionStartsAt}
                      onChange={(event) => updateStatusDraft(member.memberId, { suspensionStartsAt: event.target.value })}
                    />
                  </label>
                  <label>
                    Suspension ends
                    <input
                      type="datetime-local"
                      value={statusDraftValue.suspensionEndsAt}
                      onChange={(event) => updateStatusDraft(member.memberId, { suspensionEndsAt: event.target.value })}
                    />
                  </label>
                </div>
              )}
              <button
                type="submit"
                disabled={
                  updatingMemberStatusId === member.memberId ||
                  !statusDraftValue.status ||
                  statusDraftValue.status === member.status ||
                  !statusDraftValue.reason.trim()
                }
              >
                {updatingMemberStatusId === member.memberId ? "Updating..." : "Update status"}
              </button>
              {member.statusHistory?.length > 0 ? (
                <div className="member-detail-list">
                  {member.statusHistory.slice(0, 6).map((change) => (
                    <div key={change.statusChangeId || `${change.toStatus}-${change.createdAt}`}>
                      <strong>
                        {change.fromStatus || "new"} to {change.toStatus}
                      </strong>
                      <span>{change.reason || "No reason"} / {formatDateTime(change.createdAt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No status history recorded yet.</div>
              )}
            </form>
            </TaskDisclosure>

            <div className="member-detail-section">
              <h4>Payment history</h4>
              {memberPaymentEventsLoading && <div className="empty-state">Loading payment history...</div>}
              {!memberPaymentEventsLoading && memberPaymentEvents?.events?.length > 0 ? (
                <div className="member-detail-list">
                  {memberPaymentEvents.events.map((event) => (
                    <div key={event.auditId}>
                      <strong>{formatTimelineLabel(event)}</strong>
                      <span>
                        {reviewStatusCopy(event.paymentStatus || event.subscriptionStatus, "Recorded")}
                        {" / "}
                        {formatDateTime(event.createdAt)}
                      </span>
                      {event.checkoutSessionId && <span>Checkout linked</span>}
                      {event.subscriptionId && <span>Subscription linked</span>}
                      {event.stripeEventId && <span>Stripe update received</span>}
                    </div>
                  ))}
                </div>
              ) : !memberPaymentEventsLoading ? (
                <div className="empty-state">No payment events found.</div>
              ) : null}
            </div>

            <div className="member-detail-section">
              <h4>Receipts</h4>
              {memberPaymentReceiptsLoading && <div className="empty-state">Loading receipts...</div>}
              {!memberPaymentReceiptsLoading && memberPaymentReceipts?.receipts?.length > 0 ? (
                <div className="member-detail-list">
                  {memberPaymentReceipts.receipts.map((receipt) => (
                    <div key={receipt.receiptId}>
                      <strong>{receiptAmount(receipt)} / {receipt.description || receipt.type}</strong>
                      <span>{receipt.status} / {formatDateTime(receipt.createdAt)}</span>
                      {receipt.checkoutSessionId && <span>Checkout linked</span>}
                      <button type="button" className="secondary-button" onClick={() => downloadReceipt(receipt)}>
                        Download receipt
                      </button>
                    </div>
                  ))}
                </div>
              ) : !memberPaymentReceiptsLoading ? (
                <div className="empty-state">No receipts found.</div>
              ) : null}
            </div>

            <div className="member-detail-section">
              <h4>Reservations</h4>
              {memberDetail.reservations?.length > 0 ? (
                <div className="member-detail-list">
                  {memberDetail.reservations.map((reservation) => (
                    <div key={reservation.reservationId}>
                      <strong>{reservation.resourceSnapshot?.name || "Reservation resource"}</strong>
                      <span>
                        {reservation.status}
                        {" / "}
                        {formatDateTime(reservation.startsAt)}
                        {" - "}
                        {formatDateTime(reservation.endsAt)}
                      </span>
                      {reservation.attendance?.status && (
                        <span>
                          {reservation.attendance.status}
                          {reservation.attendance.checkedInAt ? ` / in ${formatDateTime(reservation.attendance.checkedInAt)}` : ""}
                          {reservation.attendance.checkedOutAt ? ` / out ${formatDateTime(reservation.attendance.checkedOutAt)}` : ""}
                        </span>
                      )}
                      {reservation.notes && <span>{reservation.notes}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No member reservations found.</div>
              )}
            </div>

            <TaskDisclosure
              actionLabel="Manage"
              title="Plan and billing state"
              description="Move the member to a different plan and mark when billing follow-up is needed."
            >
            <form
              className="member-detail-section member-renewal-form"
              onSubmit={(event) => {
                event.preventDefault();
                onChangeMemberPlan(member.memberId, {
                  membershipTypeId: planChangeDraftValue.membershipTypeId,
                  effectiveAt: planChangeDraftValue.effectiveAt || undefined,
                  resetTerm: planChangeDraftValue.resetTerm,
                  grantGuestPasses: planChangeDraftValue.grantGuestPasses,
                  reason: planChangeDraftValue.reason || undefined,
                  status: "active"
                });
                updatePlanChangeDraft(member.memberId, {
                  membershipTypeId: "",
                  effectiveAt: "",
                  resetTerm: true,
                  grantGuestPasses: true,
                  reason: ""
                });
              }}
            >
              <h4>Plan change</h4>
              <div className="form-row">
                <label>
                  New plan
                  <select
                    value={planChangeDraftValue.membershipTypeId}
                    onChange={(event) => updatePlanChangeDraft(member.memberId, { membershipTypeId: event.target.value })}
                    required
                  >
                    <option value="">Choose a plan</option>
                    {activeMembershipTypes
                      .filter((type) => type.membershipTypeId !== member.membershipTypeId)
                      .map((type) => (
                        <option value={type.membershipTypeId} key={type.membershipTypeId}>
                          {type.name}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  Effective
                  <input
                    type="datetime-local"
                    value={planChangeDraftValue.effectiveAt}
                    onChange={(event) => updatePlanChangeDraft(member.memberId, { effectiveAt: event.target.value })}
                  />
                </label>
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={planChangeDraftValue.resetTerm}
                    onChange={(event) => updatePlanChangeDraft(member.memberId, { resetTerm: event.target.checked })}
                  />
                  Reset term from new plan
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={planChangeDraftValue.grantGuestPasses}
                    onChange={(event) => updatePlanChangeDraft(member.memberId, { grantGuestPasses: event.target.checked })}
                  />
                  Grant new plan guest passes
                </label>
              </div>
              <label>
                Reason
                <input
                  value={planChangeDraftValue.reason}
                  onChange={(event) => updatePlanChangeDraft(member.memberId, { reason: event.target.value })}
                  placeholder="Upgrade, downgrade, corrected plan, comp change"
                />
              </label>
              {payment.subscriptionId && (
                <div className="empty-state">
                  This updates RangeGrid membership state now. Stripe subscription proration is a later billing step.
                </div>
              )}
              <button
                type="submit"
                disabled={changingMemberPlanId === member.memberId || !planChangeDraftValue.membershipTypeId}
              >
                {changingMemberPlanId === member.memberId ? "Changing..." : "Change plan"}
              </button>
              {member.planChangeHistory?.length > 0 ? (
                <div className="member-detail-list">
                  {member.planChangeHistory.slice(0, 5).map((change) => (
                    <div key={change.planChangeId}>
                      <strong>{membershipTypeName(change.fromMembershipTypeId, "Previous plan")} to {membershipTypeName(change.toMembershipTypeId, "New plan")}</strong>
                      <span>{change.reason || "No reason"} / {formatDateTime(change.changedAt)}</span>
                      {change.billingActionRequired && <span>Billing follow-up required</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No plan changes recorded yet.</div>
              )}
            </form>
            </TaskDisclosure>

            <TaskDisclosure
              actionLabel="Manage"
              title="Safety and internal flags"
              description="Add and review staff-only account, safety, incident, or admin flags."
            >
            <form
              className="member-detail-section member-renewal-form"
              onSubmit={(event) => {
                event.preventDefault();
                onAddMemberFlag(member.memberId, flagDraftValue);
                updateFlagDraft(member.memberId, {
                  type: "safety",
                  severity: "warning",
                  title: "",
                  notes: ""
                });
              }}
            >
              <h4>Internal flags</h4>
              <div className="form-row">
                <label>
                  Type
                  <select
                    value={flagDraftValue.type}
                    onChange={(event) => updateFlagDraft(member.memberId, { type: event.target.value })}
                  >
                    <option value="safety">Safety</option>
                    <option value="account">Account</option>
                    <option value="incident">Incident</option>
                    <option value="admin">Admin</option>
                  </select>
                </label>
                <label>
                  Severity
                  <select
                    value={flagDraftValue.severity}
                    onChange={(event) => updateFlagDraft(member.memberId, { severity: event.target.value })}
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                  </select>
                </label>
              </div>
              <label>
                Title
                <input
                  value={flagDraftValue.title}
                  onChange={(event) => updateFlagDraft(member.memberId, { title: event.target.value })}
                  placeholder="Safety review required"
                  required
                />
              </label>
              <label>
                Notes
                <input
                  value={flagDraftValue.notes}
                  onChange={(event) => updateFlagDraft(member.memberId, { notes: event.target.value })}
                  placeholder="Internal staff context"
                />
              </label>
              <button
                type="submit"
                disabled={addingMemberFlagId === member.memberId || !flagDraftValue.title.trim()}
              >
                {addingMemberFlagId === member.memberId ? "Adding..." : "Add flag"}
              </button>
              <div className="form-row">
                <label>
                  Filter severity
                  <select value={flagSeverityFilter} onChange={(event) => setFlagSeverityFilter(event.target.value)}>
                    <option value="">All severities</option>
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                  </select>
                </label>
                <label>
                  Filter status
                  <select value={flagStatusFilter} onChange={(event) => setFlagStatusFilter(event.target.value)}>
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="resolved">Resolved</option>
                    <option value="archived">Archived</option>
                  </select>
                </label>
              </div>
              {member.flags?.items?.length > 0 ? (
                <div className="member-detail-list">
                  {filteredFlags.slice(0, 8).map((flag) => (
                    <div key={flag.flagId}>
                      <strong>{flag.title}</strong>
                      <span>{flag.type} / {flag.severity} / {flag.status}</span>
                      {flag.notes && <span>{flag.notes}</span>}
                    </div>
                  ))}
                  {filteredFlags.length === 0 && (
                    <div className="empty-state">No internal flags match these filters.</div>
                  )}
                </div>
              ) : (
                <div className="empty-state">No internal flags recorded.</div>
              )}
            </form>
            </TaskDisclosure>

            <TaskDisclosure
              actionLabel="Manage"
              title="Qualification approvals"
              description="Grant, expire, or record lane, bay, or course qualifications for this member."
            >
            <form
              className="member-detail-section member-renewal-form"
              onSubmit={(event) => {
                event.preventDefault();
                onAddMemberQualification(member.memberId, qualificationDraftValue);
                updateQualificationDraft(member.memberId, {
                  key: "",
                  label: "",
                  catalogKey: "",
                  status: "active",
                  issuedAt: "",
                  expiresAt: "",
                  notes: ""
                });
              }}
            >
              <h4>Qualifications</h4>
              {summary.config?.settings?.qualificationCatalog?.length > 0 && (
                <label>
                  Catalog qualification
                  <select
                    value={qualificationDraftValue.catalogKey || ""}
                    onChange={(event) => applyCatalogQualification(member.memberId, event.target.value)}
                  >
                    <option value="">Manual entry</option>
                    {summary.config.settings.qualificationCatalog
                      .filter((qualification) => qualification.status !== "inactive")
                      .map((qualification) => (
                        <option key={qualification.key} value={qualification.key}>
                          {qualification.label}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              <div className="form-row">
                <label>
                  Qualification
                  <input
                    value={qualificationDraftValue.label}
                    onChange={(event) => updateQualificationDraft(member.memberId, { label: event.target.value })}
                    placeholder="Rifle bay approved"
                    required
                  />
                </label>
                <label>
                  Key
                  <input
                    value={qualificationDraftValue.key}
                    onChange={(event) => updateQualificationDraft(member.memberId, { key: event.target.value })}
                    placeholder="rifle_bay_approved"
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Status
                  <select
                    value={qualificationDraftValue.status}
                    onChange={(event) => updateQualificationDraft(member.memberId, { status: event.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="expired">Expired</option>
                    <option value="revoked">Revoked</option>
                  </select>
                </label>
                <label>
                  Issued
                  <input
                    type="datetime-local"
                    value={qualificationDraftValue.issuedAt}
                    onChange={(event) => updateQualificationDraft(member.memberId, { issuedAt: event.target.value })}
                  />
                </label>
                <label>
                  Expires
                  <input
                    type="datetime-local"
                    value={qualificationDraftValue.expiresAt}
                    onChange={(event) => updateQualificationDraft(member.memberId, { expiresAt: event.target.value })}
                  />
                </label>
              </div>
              <label>
                Notes
                <input
                  value={qualificationDraftValue.notes}
                  onChange={(event) => updateQualificationDraft(member.memberId, { notes: event.target.value })}
                  placeholder="Qualification source or staff context"
                />
              </label>
              <button
                type="submit"
                disabled={addingMemberQualificationId === member.memberId || !qualificationDraftValue.label.trim()}
              >
                {addingMemberQualificationId === member.memberId ? "Adding..." : "Add qualification"}
              </button>
              {member.qualifications?.items?.length > 0 ? (
                <div className="member-detail-list">
                  {member.qualifications.items.slice(0, 8).map((qualification) => (
                    <div key={qualification.qualificationId}>
                      <strong>{qualification.label}</strong>
                      <span>{qualification.key} / {qualificationStatusLabel(qualification)}</span>
                      {qualification.expiresAt && <span>Expires {formatDateTime(qualification.expiresAt)}</span>}
                      {qualification.notes && <span>{qualification.notes}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No qualifications recorded.</div>
              )}
            </form>
            </TaskDisclosure>

            <TaskDisclosure
              actionLabel="Manage"
              title="Household and emergency contacts"
              description="Link household members, emergency contacts, and dependent access notes."
            >
            <form
              className="member-detail-section member-renewal-form"
              onSubmit={(event) => {
                event.preventDefault();
                onAddHouseholdMember(member.memberId, householdDraftValue);
                updateHouseholdDraft(member.memberId, {
                  fullName: "",
                  email: "",
                  phone: "",
                  emergencyContactName: "",
                  emergencyContactPhone: "",
                  relationship: "family",
                  status: "active",
                  waiverStatus: "pending",
                  notes: ""
                });
              }}
            >
              <h4>Household members</h4>
              <div className="form-row">
                <label>
                  Name
                  <input
                    value={householdDraftValue.fullName}
                    onChange={(event) => updateHouseholdDraft(member.memberId, { fullName: event.target.value })}
                    placeholder="Family member name"
                    required
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={householdDraftValue.email}
                    onChange={(event) => updateHouseholdDraft(member.memberId, { email: event.target.value })}
                    placeholder="optional@example.com"
                  />
                </label>
                <label>
                  Phone
                  <input
                    value={householdDraftValue.phone}
                    onChange={(event) => updateHouseholdDraft(member.memberId, { phone: event.target.value })}
                    placeholder="Optional phone"
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Relationship
                  <input
                    value={householdDraftValue.relationship}
                    onChange={(event) => updateHouseholdDraft(member.memberId, { relationship: event.target.value })}
                    placeholder="spouse, dependent, child"
                  />
                </label>
                <label>
                  Status
                  <select
                    value={householdDraftValue.status}
                    onChange={(event) => updateHouseholdDraft(member.memberId, { status: event.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
              </div>
              <div className="form-row">
                <label>
                  Emergency contact
                  <input
                    value={householdDraftValue.emergencyContactName}
                    onChange={(event) => updateHouseholdDraft(member.memberId, { emergencyContactName: event.target.value })}
                    placeholder="Emergency contact name"
                  />
                </label>
                <label>
                  Emergency phone
                  <input
                    value={householdDraftValue.emergencyContactPhone}
                    onChange={(event) => updateHouseholdDraft(member.memberId, { emergencyContactPhone: event.target.value })}
                    placeholder="Emergency contact phone"
                  />
                </label>
              </div>
              <label>
                Notes
                <input
                  value={householdDraftValue.notes}
                  onChange={(event) => updateHouseholdDraft(member.memberId, { notes: event.target.value })}
                  placeholder="Waiver, dependent, access note"
                />
              </label>
              <button
                type="submit"
                disabled={addingHouseholdMemberId === member.memberId || !householdDraftValue.fullName.trim()}
              >
                {addingHouseholdMemberId === member.memberId ? "Adding..." : "Add household member"}
              </button>
              {member.household?.members?.length > 0 ? (
                <div className="member-detail-list">
                  {member.household.members.map((householdMember) => (
                    <div key={householdMember.householdMemberId}>
                      <strong>{householdMember.fullName}</strong>
                      <span>{householdMember.relationship} / {householdMember.status} / waiver {householdMember.waiverStatus}</span>
                      {householdMember.email && <span>{householdMember.email}</span>}
                      {householdMember.phone && <span>Phone: {householdMember.phone}</span>}
                      {(householdMember.emergencyContactName || householdMember.emergencyContactPhone) && (
                        <span>Emergency: {[householdMember.emergencyContactName, householdMember.emergencyContactPhone].filter(Boolean).join(" / ")}</span>
                      )}
                      {householdMember.notes && <span>{householdMember.notes}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No household members linked yet.</div>
              )}
            </form>
            </TaskDisclosure>

            <TaskDisclosure
              actionLabel="Manage"
              title="Manual term renewal"
              description="Extend or correct a term when staff handles renewal outside normal checkout."
            >
            <form
              className="member-detail-section member-renewal-form"
              onSubmit={(event) => {
                event.preventDefault();
                onRenewMember(member.memberId, {
                  startsAt: renewalDraftValue.startsAt || undefined,
                  endsAt: renewalDraftValue.endsAt || undefined,
                  reason: renewalDraftValue.reason || undefined,
                  status: "active"
                });
              }}
            >
              <h4>Manual renewal</h4>
              <div className="form-row">
                <label>
                  Term starts
                  <input
                    type="datetime-local"
                    value={renewalDraftValue.startsAt}
                    onChange={(event) => updateRenewalDraft(member.memberId, { startsAt: event.target.value })}
                  />
                </label>
                <label>
                  Term ends
                  <input
                    type="datetime-local"
                    value={renewalDraftValue.endsAt}
                    onChange={(event) => updateRenewalDraft(member.memberId, { endsAt: event.target.value })}
                  />
                </label>
              </div>
              <label>
                Reason
                <input
                  value={renewalDraftValue.reason}
                  onChange={(event) => updateRenewalDraft(member.memberId, { reason: event.target.value })}
                  placeholder="Counter renewal, comp extension, corrected expiration"
                />
              </label>
              <button type="submit" disabled={renewingMemberId === member.memberId}>
                {renewingMemberId === member.memberId ? "Renewing..." : "Renew member"}
              </button>
            </form>
            </TaskDisclosure>

            <TaskDisclosure
              actionLabel="Manage"
              title="Guest pass ledger"
              description="Use, grant, or correct guest pass balances with a traceable reason."
            >
            <form
              className="member-detail-section member-renewal-form"
              onSubmit={(event) => {
                event.preventDefault();
                onUpdateMemberGuestPasses(member.memberId, {
                  action: guestPassDraftValue.action,
                  quantity: Number.parseInt(guestPassDraftValue.quantity || "0", 10),
                  reason: guestPassDraftValue.reason || undefined
                });
                updateGuestPassDraft(member.memberId, { action: "use", quantity: "1", reason: "" });
              }}
            >
              <h4>Guest pass ledger</h4>
              <div className="member-detail-grid">
                <div>
                  <span>Granted</span>
                  <strong>{guestPassLedger.granted || 0}</strong>
                  <small>Plan and staff grants</small>
                </div>
                <div>
                  <span>Used</span>
                  <strong>{guestPassLedger.used || 0}</strong>
                  <small>Consumed at check-in or staff action</small>
                </div>
                <div>
                  <span>Adjustments</span>
                  <strong>{guestPassLedger.adjusted || 0}</strong>
                  <small>Signed corrections</small>
                </div>
                <div>
                  <span>Remaining</span>
                  <strong>{guestPassLedger.remaining || 0}</strong>
                  <small>Current available balance</small>
                </div>
              </div>
              <div className="form-row">
                <label>
                  Action
                  <select
                    value={guestPassDraftValue.action}
                    onChange={(event) => updateGuestPassDraft(member.memberId, { action: event.target.value })}
                  >
                    <option value="use">Use pass</option>
                    <option value="grant">Grant pass</option>
                    <option value="adjust">Adjust balance</option>
                  </select>
                </label>
                <label>
                  Quantity
                  <input
                    type="number"
                    value={guestPassDraftValue.quantity}
                    onChange={(event) => updateGuestPassDraft(member.memberId, { quantity: event.target.value })}
                  />
                </label>
              </div>
              <label>
                Reason
                <input
                  value={guestPassDraftValue.reason}
                  onChange={(event) => updateGuestPassDraft(member.memberId, { reason: event.target.value })}
                  placeholder="Guest checked in, comp pass, correction"
                />
              </label>
              <button
                type="submit"
                disabled={updatingGuestPassMemberId === member.memberId || !guestPassDraftValue.quantity}
              >
                {updatingGuestPassMemberId === member.memberId ? "Updating..." : "Update guest passes"}
              </button>
              {guestPassLedger.entries?.length > 0 ? (
                <div className="member-detail-list">
                  {guestPassLedger.entries.slice(0, 8).map((entry) => (
                    <div key={entry.entryId}>
                      <strong>{reviewStatusCopy(entry.action, "Guest pass update")} {entry.quantity}</strong>
                      <span>{entry.reason || "No reason"} / {formatDateTime(entry.createdAt)}</span>
                      <span>{entry.actorUserId ? "Team member" : timelineSourceLabel(entry.source)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No guest pass activity yet.</div>
              )}
              {member.guestVisits?.items?.length > 0 && (
                <div className="member-detail-list">
                  <h5>Guest visits</h5>
                  {member.guestVisits.items.slice(0, 8).map((visit) => (
                    <div key={visit.visitId}>
                      <strong>{visit.guestName || "Guest visit"}</strong>
                      <span>{visit.guestEmail || "No guest email"} / {visit.status}</span>
                      <span>
                        Waiver {visit.waiverAcknowledgedAt ? `acknowledged ${formatDateTime(visit.waiverAcknowledgedAt)}` : "not acknowledged"}
                      </span>
                      {visit.guestPassEntryId && <span>Guest pass used</span>}
                    </div>
                  ))}
                </div>
              )}
            </form>
            </TaskDisclosure>

            <div className="member-detail-section">
              <h4>Staff notes</h4>
              {member.noteHistory?.length > 0 ? (
                <div className="member-detail-list">
                  {member.noteHistory.map((note) => (
                    <div key={note.noteId}>
                      <strong>{note.body}</strong>
                      <span>{note.actorUserId ? "Team member" : "Staff note"} / {formatDateTime(note.createdAt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No staff notes yet.</div>
              )}
            </div>

            <div className="member-detail-section">
              <h4>Related notifications</h4>
              {relatedNotifications.length > 0 ? (
                <>
                <div className="member-detail-list">
                  {visibleRelatedNotifications.map((notification) => (
                    <div key={notification.notificationId}>
                      <strong>{notification.subject}</strong>
                      <span>{reviewStatusCopy(notification.type, "Range message")} / {reviewStatusCopy(notification.status, "Queued")} / {formatDateTime(notification.createdAt)}</span>
                      {notification.reviewedAt && (
                        <span>Reviewed {formatDateTime(notification.reviewedAt)}</span>
                      )}
                    </div>
                  ))}
                </div>
                {relatedNotifications.length > visibleRelatedNotifications.length && (
                  <div className="member-detail-list-footer">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setMemberActivityExpanded((current) => ({ ...current, notifications: true }))}
                    >
                      Show {relatedNotifications.length - visibleRelatedNotifications.length} older messages
                    </button>
                  </div>
                )}
                {memberActivityExpanded.notifications && relatedNotifications.length > 6 && (
                  <div className="member-detail-list-footer">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setMemberActivityExpanded((current) => ({ ...current, notifications: false }))}
                    >
                      Show recent messages only
                    </button>
                  </div>
                )}
                </>
              ) : (
                <div className="empty-state">No related notifications found.</div>
              )}
            </div>

            <div className="member-detail-section">
              <h4>Timeline</h4>
              {memberTimelineLoading && <div className="empty-state">Loading timeline...</div>}
              {!memberTimelineLoading && timelineItems.length > 0 ? (
                <>
                <div className="member-detail-list member-timeline-list">
                  {visibleTimelineItems.map((item) => (
                    <div key={item.id}>
                      <strong>{formatTimelineLabel(item)}</strong>
                      <span>
                        {timelineSourceLabel(item.source)}
                        {item.status ? ` / ${reviewStatusCopy(item.status)}` : ""}
                        {" / "}
                        {formatDateTime(item.createdAt || item.updatedAt)}
                      </span>
                      {item.subject && <span>{item.subject}</span>}
                      {formatTimelineDetail(item) && <span>{formatTimelineDetail(item)}</span>}
                      {item.actorUserId && <span>Updated by team member</span>}
                    </div>
                  ))}
                </div>
                {timelineItems.length > visibleTimelineItems.length && (
                  <div className="member-detail-list-footer">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setMemberActivityExpanded((current) => ({ ...current, timeline: true }))}
                    >
                      Show {timelineItems.length - visibleTimelineItems.length} older updates
                    </button>
                  </div>
                )}
                {memberActivityExpanded.timeline && timelineItems.length > 8 && (
                  <div className="member-detail-list-footer">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setMemberActivityExpanded((current) => ({ ...current, timeline: false }))}
                    >
                      Show recent updates only
                    </button>
                  </div>
                )}
                </>
              ) : !memberTimelineLoading ? (
                <div className="empty-state">No timeline activity found.</div>
              ) : null}
            </div>
          </>
        )}
      </section>
    );
  }

  const applicantSearchQuery = applicantSearch.trim().toLowerCase();
  const filteredApplicants = applicantMembers
    .filter((member) => {
      const matchesSearch = !applicantSearchQuery || [
        member.fullName,
        member.email,
        member.signupIntentId,
        member.memberId,
        member.review?.reason,
        member.notes,
        member.review?.notes
      ].some((value) => String(value || "").toLowerCase().includes(applicantSearchQuery));
      if (!matchesSearch) {
        return false;
      }

      if (applicantFilter === "pending_review") {
        return member.status === "pending" || member.review?.status === "pending" || member.planChangeRequest?.status === "pending";
      }
      if (applicantFilter === "needs_waiver") {
        return requirementOpen(member, "waiver");
      }
      if (applicantFilter === "needs_prequalification") {
        return requirementOpen(member, "prequalification") || checklistOpen(member, "prequalification");
      }
      if (applicantFilter === "needs_info") {
        return member.review?.status === "needs_info";
      }
      if (applicantFilter === "ready") {
        return applicantReady(member);
      }
      if (applicantFilter === "rejected") {
        return member.review?.status === "rejected" || member.status === "inactive";
      }
      if (applicantFilter === "active") {
        return member.status === "active";
      }
      return true;
    })
    .sort((left, right) => {
      const readyDelta = Number(applicantReady(right)) - Number(applicantReady(left));
      if (readyDelta !== 0) {
        return readyDelta;
      }
      return memberSortTime(right) - memberSortTime(left);
    });

  return (
    <section className="panel module-panel" id="membership">
      {loading && <div className="empty-state">Loading membership workspace...</div>}

      {!loading && summary && (
        <>
          <nav className="console-section-nav" aria-label="Membership sections">
            {membershipSections.map((section) => (
              <button
                type="button"
                className={activeMembershipSection === section.id ? "is-active" : ""}
                key={section.id}
                onClick={() => switchMembershipSection(section.id)}
              >
                <span>{section.label}</span>
                <small>{section.meta}</small>
              </button>
            ))}
          </nav>

          <div className="membership-section-overview">
            <div>
              <span className="section-kicker">{activeMembershipContext.eyebrow}</span>
              <h3>{activeMembershipContext.title}</h3>
              <p>{activeMembershipContext.description}</p>
            </div>
            {activeMembershipSection !== "requirements" && (
              <div className="membership-context-metrics">
                {activeMembershipContext.metrics.map((metric) => (
                  <div key={metric.label}>
                    <span>{metric.label}</span>
                    <strong>{metric.value}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>

          {activeMembershipSection === "application" && (
            <form className="membership-settings-panel application-form-builder" onSubmit={(event) => {
              event.preventDefault();
              saveApplicationForm(applicationForm, false);
            }}>
              <div className="panel-header application-form-builder-header">
                <div>
                  <h3>Application form</h3>
                  <p>Build the screening questions applicants complete before owner review.</p>
                </div>
                <div className="application-form-actions">
                  <button type="button" className="secondary-button" onClick={loadFirearmsScreeningTemplate}>
                    Use firearms template
                  </button>
                  <button type="button" className="secondary-button" onClick={addApplicationSection}>
                    Add section
                  </button>
                  <button type="submit" className="secondary-button" disabled={savingMembershipConfig || applicationQuestionCount === 0}>
                    {savingMembershipConfig ? "Saving..." : "Save draft"}
                  </button>
                  <button
                    type="button"
                    disabled={applicationPublishDisabled}
                    onClick={() => saveApplicationForm(applicationForm, true)}
                  >
                    {savingMembershipConfig ? "Publishing..." : "Publish form"}
                  </button>
                </div>
              </div>

              <div className="application-form-status-strip">
                <div>
                  <span>Status</span>
                  <strong>{applicationPublished ? "Published for applicants" : applicationQuestionCount > 0 ? "Draft" : "Not built"}</strong>
                </div>
                <div>
                  <span>Questions</span>
                  <strong>{applicationQuestionCount}</strong>
                </div>
                <div>
                  <span>Applicant gate</span>
                  <strong>{applicationPublished ? "Required before review" : "Not active"}</strong>
                </div>
              </div>

              {applicationFormIssues.length > 0 && (
                <div className="application-form-issues" role="status">
                  <strong>Fix before publishing</strong>
                  <ul>
                    {applicationFormIssues.slice(0, 5).map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="application-form-shell">
                <section className="application-form-editor">
                  <label>
                    Form title
                    <input
                      value={applicationForm.title}
                      onChange={(event) => updateApplicationFormField("title", event.target.value)}
                      placeholder="Membership Application"
                    />
                  </label>
                  <label>
                    Intro text
                    <textarea
                      value={applicationForm.description}
                      onChange={(event) => updateApplicationFormField("description", event.target.value)}
                      placeholder="Tell applicants what this form is for."
                      rows="3"
                    />
                  </label>

                  {applicationForm.sections.length === 0 ? (
                    <div className="requirements-item-empty application-form-empty">
                      Start with the firearms template or add a section manually.
                    </div>
                  ) : (
                    <div className="application-section-stack">
                      {applicationForm.sections.map((section, sectionIndex) => (
                        <article className="application-section-card" key={section.sectionId || sectionIndex}>
                          <div className="application-section-card-header">
                            <div>
                              <span>Section {sectionIndex + 1}</span>
                              <input
                                value={section.title}
                                onChange={(event) => updateApplicationSection(sectionIndex, { title: event.target.value })}
                                aria-label={`Section ${sectionIndex + 1} title`}
                              />
                            </div>
                            <div className="row-actions">
                              <button type="button" className="secondary-button" onClick={() => addApplicationQuestion(sectionIndex)}>
                                Add question
                              </button>
                              <button type="button" className="danger-button" onClick={() => removeApplicationSection(sectionIndex)}>
                                Remove
                              </button>
                            </div>
                          </div>
                          <label>
                            Section note
                            <input
                              value={section.description || ""}
                              onChange={(event) => updateApplicationSection(sectionIndex, { description: event.target.value })}
                              placeholder="Optional note shown above this section"
                            />
                          </label>

                          <div className="application-question-stack">
                            {(section.questions || []).map((question, questionIndex) => (
                              <article className="application-question-card" key={question.questionId || questionIndex}>
                                <div className="application-question-grid">
                                  <label>
                                    Question
                                    <input
                                      value={question.label}
                                      onChange={(event) => updateApplicationQuestion(sectionIndex, questionIndex, { label: event.target.value })}
                                      placeholder="Question label"
                                    />
                                  </label>
                                  <label>
                                    Type
                                    <select
                                      value={question.type}
                                      onChange={(event) => updateApplicationQuestion(sectionIndex, questionIndex, { type: event.target.value })}
                                    >
                                      {APPLICATION_QUESTION_TYPES.map((type) => (
                                        <option value={type.value} key={type.value}>{type.label}</option>
                                      ))}
                                    </select>
                                  </label>
                                  <label className="requirements-switch-row application-required-row">
                                    <input
                                      type="checkbox"
                                      checked={question.required !== false}
                                      onChange={(event) => updateApplicationQuestion(sectionIndex, questionIndex, { required: event.target.checked })}
                                    />
                                    <span>
                                      <strong>Required</strong>
                                      <small>Applicant must answer</small>
                                    </span>
                                  </label>
                                </div>
                                <label>
                                  Help text
                                  <input
                                    value={question.helpText || ""}
                                    onChange={(event) => updateApplicationQuestion(sectionIndex, questionIndex, { helpText: event.target.value })}
                                    placeholder="Optional helper text"
                                  />
                                </label>
                                {["single_choice", "multi_choice"].includes(question.type) && (
                                  <div className="application-choice-editor">
                                    <label>
                                      Choices
                                      <textarea
                                        value={(question.options || []).map((option) => option.label).join("\n")}
                                        onChange={(event) => updateApplicationQuestionOptions(sectionIndex, questionIndex, event.target.value)}
                                        placeholder="One choice per line"
                                        rows="5"
                                      />
                                    </label>
                                    {question.type === "multi_choice" && (
                                      <label>
                                        Max selections
                                        <input
                                          type="number"
                                          min="0"
                                          max={Math.max((question.options || []).length, 1)}
                                          value={question.maxSelections || 0}
                                          onChange={(event) => updateApplicationQuestion(sectionIndex, questionIndex, { maxSelections: Number.parseInt(event.target.value || "0", 10) })}
                                        />
                                        <small>Use 0 for no limit.</small>
                                      </label>
                                    )}
                                    {question.type === "multi_choice" && (
                                      <label className="requirements-switch-row application-required-row">
                                        <input
                                          type="checkbox"
                                          checked={Boolean(question.allowOther)}
                                          onChange={(event) => updateApplicationQuestion(sectionIndex, questionIndex, { allowOther: event.target.checked })}
                                        />
                                        <span>
                                          <strong>Allow Other</strong>
                                          <small>Shows a text field when selected</small>
                                        </span>
                                      </label>
                                    )}
                                  </div>
                                )}
                                {question.type === "yes_no" && (
                                  <div className="application-choice-editor">
                                    <label className="requirements-switch-row application-required-row">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(question.conditionalDetails?.enabled)}
                                        onChange={(event) => updateApplicationQuestion(sectionIndex, questionIndex, {
                                          conditionalDetails: event.target.checked
                                            ? {
                                                enabled: true,
                                                whenValue: "yes",
                                                label: question.conditionalDetails?.label || "If yes, please provide details",
                                                required: question.conditionalDetails?.required !== false
                                              }
                                            : undefined
                                        })}
                                      />
                                      <span>
                                        <strong>Ask for details on Yes</strong>
                                        <small>Useful for legal or safety explanations</small>
                                      </span>
                                    </label>
                                    {question.conditionalDetails?.enabled && (
                                      <label>
                                        Details prompt
                                        <input
                                          value={question.conditionalDetails.label || ""}
                                          onChange={(event) => updateApplicationQuestion(sectionIndex, questionIndex, {
                                            conditionalDetails: {
                                              ...question.conditionalDetails,
                                              enabled: true,
                                              whenValue: "yes",
                                              label: event.target.value
                                            }
                                          })}
                                        />
                                      </label>
                                    )}
                                  </div>
                                )}
                                <div className="row-actions application-question-actions">
                                  <button type="button" className="danger-button" onClick={() => removeApplicationQuestion(sectionIndex, questionIndex)}>
                                    Remove question
                                  </button>
                                </div>
                              </article>
                            ))}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </section>

                <aside className="application-form-preview" aria-label="Applicant preview">
                  <span className="section-kicker">Applicant preview</span>
                  <h4>{applicationForm.title || "Membership Application"}</h4>
                  {applicationForm.description && <p>{applicationForm.description}</p>}
                  {applicationForm.sections.map((section) => (
                    <div className="application-preview-section" key={section.sectionId}>
                      <strong>{section.title}</strong>
                      {section.description && <span>{section.description}</span>}
                      {(section.questions || []).map((question) => (
                        <div className="application-preview-question" key={question.questionId}>
                          <span>{question.label}{question.required !== false ? " *" : ""}</span>
                          <small>
                            {APPLICATION_QUESTION_TYPES.find((type) => type.value === question.type)?.label || "Question"}
                            {question.maxSelections > 0 ? ` / max ${question.maxSelections}` : ""}
                          </small>
                        </div>
                      ))}
                    </div>
                  ))}
                </aside>
              </div>
            </form>
          )}

          {activeMembershipSection === "requirements" && (
          <>
          <form className="membership-settings-panel requirements-workbench" onSubmit={onSaveMembershipConfig}>
            <div className="panel-header requirements-workbench-header">
              <div>
                <h3>Membership requirements</h3>
                <p>Separate new-member entry review from reusable access qualifications.</p>
              </div>
            </div>

            <div className="requirements-mode-tabs" role="tablist" aria-label="Requirement areas">
              <button
                type="button"
                className={requirementsSubsection === "entry" ? "is-active" : ""}
                onClick={() => {
                  setRequirementsSubsection("entry");
                  setRequirementsEditTarget("");
                  setRequirementsAddOpen(false);
                  setRequirementGateDraft((current) => ({
                    ...current,
                    type: current.type === "qualification" ? "onboarding" : current.type
                  }));
                }}
              >
                <strong>Entry requirements</strong>
                <span>Waiver, onboarding, and background review</span>
              </button>
              <button
                type="button"
                className={requirementsSubsection === "access" ? "is-active" : ""}
                onClick={() => {
                  setRequirementsSubsection("access");
                  setRequirementsEditTarget("");
                  setRequirementsAddOpen(false);
                  setRequirementGateDraft((current) => ({
                    ...current,
                    type: "qualification"
                  }));
                }}
              >
                <strong>Access control</strong>
                <span>Qualifications used by resources and events</span>
              </button>
            </div>

            <div className="requirements-approval-strip">
              <div>
                <span>Activation mode</span>
                <strong>
                  {membershipConfigForm.approvalMode === "manual"
                    ? "Staff approves each member"
                    : "Auto-activate when clear"}
                </strong>
              </div>
              {requirementsEditTarget === "activation" ? (
                <div className="requirements-activation-editor">
                  <label className="requirements-mode-toggle">
                    <input
                      type="checkbox"
                      checked={membershipConfigForm.approvalMode !== "manual"}
                      onChange={(event) => setMembershipConfigForm((current) => ({
                        ...current,
                        approvalMode: event.target.checked ? "auto_after_requirements" : "manual"
                      }))}
                    />
                    <span>
                      <strong>Auto activate</strong>
                      <small>After gates are clear</small>
                    </span>
                  </label>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setRequirementsEditTarget("")}
                  >
                    Done
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setRequirementsEditTarget("activation")}
                >
                  Edit activation
                </button>
              )}
            </div>

            {requirementsAddOpen && (
            <div className="requirements-add-overlay" role="presentation">
              <div
                className="requirements-add-panel"
                role="dialog"
                aria-modal="true"
                aria-label={requirementGateDraft.type === "qualification" ? "Add access qualification" : "Add review item"}
              >
                <div className="requirements-policy-heading">
                  <div>
                    <span>
                      {requirementGateDraft.type === "qualification"
                        ? "Access builder"
                        : requirementGateDraft.type === "background"
                          ? "Background builder"
                          : "Onboarding builder"}
                    </span>
                    <h4>
                      {requirementGateDraft.type === "qualification"
                        ? "New access qualification"
                        : requirementGateDraft.type === "background"
                          ? "New background-check task"
                          : "New onboarding step"}
                    </h4>
                    <p>
                      {requirementGateDraft.type === "qualification"
                        ? "Create a qualification key with the same fields shown in the qualification catalog."
                        : "Create the same compact step row shown in this requirement card."}
                    </p>
                  </div>
                </div>
                <div className="requirements-add-gate">
                  <label>
                    {requirementGateDraft.type === "qualification" ? "Qualification name" : "Step name"}
                    <input
                      value={requirementGateDraft.label}
                      onChange={(event) => setRequirementGateDraft((current) => ({
                        ...current,
                        label: event.target.value
                      }))}
                      placeholder={requirementGateDraft.type === "qualification" ? "Example: 1,000-yard qualified" : "Example: Safety orientation completed"}
                    />
                  </label>
                  {requirementGateDraft.type === "qualification" && (
                    <>
                      <label>
                        Qualification key
                        <input
                          value={requirementGateDraft.key}
                          onChange={(event) => setRequirementGateDraft((current) => ({
                            ...current,
                            key: event.target.value
                          }))}
                          placeholder={slugifyRequirementKey(requirementGateDraft.label) || "1000_yard"}
                        />
                      </label>
                      <label>
                        Expires after
                        <div className="input-with-suffix">
                          <input
                            type="number"
                            min="0"
                            max="3650"
                            value={requirementGateDraft.expiresInDays}
                            onChange={(event) => setRequirementGateDraft((current) => ({
                              ...current,
                              expiresInDays: event.target.value
                            }))}
                          />
                          <span>days</span>
                        </div>
                      </label>
                    </>
                  )}
                  <label>
                    {requirementGateDraft.type === "qualification" ? "Staff note" : "Instructions"}
                    <input
                      value={requirementGateDraft.note}
                      onChange={(event) => setRequirementGateDraft((current) => ({
                        ...current,
                        note: event.target.value
                      }))}
                      placeholder={requirementGateDraft.type === "qualification" ? "Where this qualification applies" : "Instructions for staff review"}
                    />
                  </label>
                </div>
                <div className="requirements-add-actions">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setRequirementsAddOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="button" onClick={handleAddRequirementGate}>
                    {requirementGateDraft.type === "qualification" ? "Add qualification" : "Add step"}
                  </button>
                </div>
              </div>
            </div>
            )}

            {requirementsSubsection === "entry" && (
            <div className="requirements-policy-grid">
              <section className="requirements-policy-card">
                <div className="requirements-policy-heading">
                  <div>
                    <span>Gate 01</span>
                    <h4>Waiver policy</h4>
                    <p>Control whether the member must acknowledge a waiver and how often it expires.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRequirementsEditTarget((current) => current === "waiver" ? "" : "waiver")}
                  >
                    {requirementsEditTarget === "waiver" ? "Done" : "Edit"}
                  </button>
                </div>
                {requirementsEditTarget === "waiver" ? (
                  <>
                    <label className="requirements-switch-row">
                      <input
                        type="checkbox"
                        checked={membershipConfigForm.waiverRequired}
                        onChange={(event) => setMembershipConfigForm((current) => ({
                          ...current,
                          waiverRequired: event.target.checked
                        }))}
                      />
                      <span>
                        <strong>Require waiver before member activation</strong>
                        <small>Applicants remain in review until the waiver is marked complete.</small>
                      </span>
                    </label>
                    <label>
                      Staff/member instructions
                      <textarea
                        value={membershipConfigForm.waiverInstructions}
                        onChange={(event) => setMembershipConfigForm((current) => ({
                          ...current,
                          waiverInstructions: event.target.value
                        }))}
                        placeholder="Explain where the member signs the waiver or what staff should verify."
                        rows="3"
                      />
                    </label>
                    <label>
                      Waiver expires after
                      <div className="input-with-suffix">
                        <input
                          type="number"
                          min="0"
                          max="3650"
                          value={membershipConfigForm.waiverExpiresInDays}
                          onChange={(event) => setMembershipConfigForm((current) => ({
                            ...current,
                            waiverExpiresInDays: event.target.value
                          }))}
                          placeholder="365"
                        />
                        <span>days</span>
                      </div>
                      <small>Use 365 for annual resigning, or 0 if the waiver does not expire.</small>
                    </label>
                  </>
                ) : (
                  <div className="requirements-policy-strip">
                    <span className={membershipConfigForm.waiverRequired ? "is-required" : ""}>
                      {membershipConfigForm.waiverRequired ? "Required before activation" : "Not required"}
                    </span>
                    <span>
                      {Number.parseInt(membershipConfigForm.waiverExpiresInDays || "0", 10) > 0
                        ? `Renew every ${membershipConfigForm.waiverExpiresInDays} days`
                        : "No renewal cadence"}
                    </span>
                    {membershipConfigForm.waiverInstructions && (
                      <p className="requirements-guidance">
                        <strong>Staff guidance</strong>
                        {membershipConfigForm.waiverInstructions}
                      </p>
                    )}
                  </div>
                )}

                <div className="document-upload-box requirements-document-box">
                  <div>
                    <strong>Current waiver document</strong>
                    {membershipConfigForm.waiverDocument ? (
                      <span>
                        {membershipConfigForm.waiverDocument.fileName || "Uploaded waiver"}
                        {membershipConfigForm.waiverDocument.versionLabel
                          ? ` (${membershipConfigForm.waiverDocument.versionLabel})`
                          : " (new version pending save)"}
                      </span>
                    ) : (
                      <span>No waiver uploaded yet.</span>
                    )}
                    {membershipConfigForm.waiverDocument?.effectiveAt && (
                      <span>Effective {new Date(membershipConfigForm.waiverDocument.effectiveAt).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="requirements-document-actions">
                    <label className="secondary-button">
                      {uploadingWaiver
                        ? "Uploading..."
                        : membershipConfigForm.waiverDocument
                          ? "Update waiver"
                          : "Add waiver"}
                      <input
                        type="file"
                        accept="application/pdf,image/png,image/jpeg,text/plain"
                        disabled={uploadingWaiver}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) {
                            onUploadWaiver(file);
                          }
                          event.target.value = "";
                        }}
                      />
                    </label>
                    {requirementsEditTarget === "waiver" && membershipConfigForm.waiverDocument && (
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => setMembershipConfigForm((current) => ({ ...current, waiverDocument: null }))}
                        >
                          Remove
                        </button>
                    )}
                  </div>
                  {membershipConfigForm.waiverDocumentHistory?.length > 0 && (
                    <details className="requirements-version-history">
                      <summary>Previous versions</summary>
                      <div className="compact-list">
                        {membershipConfigForm.waiverDocumentHistory.slice(0, 5).map((document) => (
                          <span key={document.documentId || document.key}>
                            {document.versionLabel || `v${document.version || "?"}`} / {document.fileName || "Waiver"}
                            {document.retiredAt ? ` / retired ${new Date(document.retiredAt).toLocaleDateString()}` : ""}
                          </span>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </section>

              <section className="requirements-policy-card">
                <div className="requirements-policy-heading">
                  <div>
                    <span>Gate 02</span>
                    <h4>Onboarding checklist</h4>
                    <p>Use this for qualification, orientation, or safety steps that staff must review.</p>
                  </div>
                  <div className="requirements-card-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => openRequirementBuilder("onboarding")}
                    >
                      Add step
                    </button>
                    <button
                      type="button"
                      onClick={() => setRequirementsEditTarget((current) => current === "onboarding" ? "" : "onboarding")}
                    >
                      {requirementsEditTarget === "onboarding" ? "Done" : "Edit"}
                    </button>
                  </div>
                </div>
                {requirementsEditTarget === "onboarding" ? (
                  <>
                    <label className="requirements-switch-row">
                      <input
                        type="checkbox"
                        checked={membershipConfigForm.prequalificationRequired}
                        onChange={(event) => setMembershipConfigForm((current) => ({
                          ...current,
                          prequalificationRequired: event.target.checked
                        }))}
                      />
                      <span>
                        <strong>Require onboarding review before activation</strong>
                        <small>Applicants stay in review until onboarding requirements are complete.</small>
                      </span>
                    </label>
                    <label>
                      Onboarding instructions
                      <textarea
                        value={membershipConfigForm.prequalificationInstructions}
                        onChange={(event) => setMembershipConfigForm((current) => ({
                          ...current,
                          prequalificationInstructions: event.target.value
                        }))}
                        placeholder="Describe orientation, certification, safety check, or other prerequisites."
                        rows="3"
                      />
                    </label>
                  </>
                ) : (
                  <div className="requirements-policy-strip">
                    <span className={membershipConfigForm.prequalificationRequired ? "is-required" : ""}>
                      {membershipConfigForm.prequalificationRequired ? "Required before activation" : "Not required"}
                    </span>
                    <span>{onboardingChecklistItems.length} step{onboardingChecklistItems.length === 1 ? "" : "s"} configured</span>
                    {membershipConfigForm.prequalificationInstructions && (
                      <p className="requirements-guidance">
                        <strong>Staff guidance</strong>
                        {membershipConfigForm.prequalificationInstructions}
                      </p>
                    )}
                  </div>
                )}
                <div className="requirements-item-list">
                  <div className="requirements-item-list-header">
                    <strong>Checklist steps</strong>
                    <span>{onboardingChecklistItems.length} configured</span>
                  </div>
                  {onboardingChecklistItems.length > 0 ? (
                    onboardingChecklistItems.map((item, index) => (
                      <div className="requirements-item-row" key={item.id}>
                        <div>
                          <strong>{item.label}</strong>
                          {item.instructions && <small>{item.instructions}</small>}
                        </div>
                        {requirementsEditTarget === "onboarding" && (
                          <button type="button" onClick={() => removeChecklistItem("prequalificationChecklistText", index)}>
                            Remove
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="requirements-item-empty">
                      Use Add step to define the onboarding tasks staff must clear.
                    </div>
                  )}
                </div>
              </section>

              <section className="requirements-policy-card">
                <div className="requirements-policy-heading">
                  <div>
                    <span>Gate 03</span>
                    <h4>Background check</h4>
                    <p>Use Checkr as the screening engine while the range keeps the final membership decision.</p>
                  </div>
                  <div className="requirements-card-actions">
                    <button
                      type="button"
                      onClick={() => setRequirementsEditTarget((current) => current === "background" ? "" : "background")}
                    >
                      {requirementsEditTarget === "background" ? "Done" : "Edit"}
                    </button>
                  </div>
                </div>
                {requirementsEditTarget === "background" ? (
                  <>
                    <label className="requirements-switch-row">
                      <input
                        type="checkbox"
                        checked={membershipConfigForm.backgroundCheckRequired}
                        onChange={(event) => setMembershipConfigForm((current) => ({
                          ...current,
                          backgroundCheckRequired: event.target.checked
                        }))}
                      />
                      <span>
                        <strong>Require background-check clearance before activation</strong>
                        <small>Applicants stay in review until staff records a clear decision.</small>
                      </span>
                    </label>
                    <label>
                      Background-check instructions
                      <textarea
                        value={membershipConfigForm.backgroundCheckInstructions}
                        onChange={(event) => setMembershipConfigForm((current) => ({
                          ...current,
                          backgroundCheckInstructions: event.target.value
                        }))}
                        placeholder="Example: National felony check required before orientation access is approved."
                        rows="3"
                      />
                    </label>
                    <div className="requirements-form-grid">
                      <label>
                        Checkr environment
                        <select
                          value={membershipConfigForm.backgroundCheckEnvironment || "test"}
                          onChange={(event) => setMembershipConfigForm((current) => ({
                            ...current,
                            backgroundCheckProvider: "checkr",
                            backgroundCheckEnvironment: event.target.value
                          }))}
                        >
                          <option value="test">Test mode</option>
                          <option value="production">Production mode</option>
                        </select>
                      </label>
                      <label>
                        Screening package
                        <input
                          value={membershipConfigForm.backgroundCheckPackageId || ""}
                          onChange={(event) => setMembershipConfigForm((current) => ({
                            ...current,
                            backgroundCheckProvider: "checkr",
                            backgroundCheckPackageId: event.target.value
                          }))}
                          placeholder="Example: ssn_trace_plus"
                        />
                      </label>
                      <label>
                        Package name
                        <input
                          value={membershipConfigForm.backgroundCheckPackageName || ""}
                          onChange={(event) => setMembershipConfigForm((current) => ({
                            ...current,
                            backgroundCheckProvider: "checkr",
                            backgroundCheckPackageName: event.target.value
                          }))}
                          placeholder="Standard firearms screen"
                        />
                      </label>
                      <label>
                        Work location
                        <input
                          value={membershipConfigForm.backgroundCheckWorkLocationId || ""}
                          onChange={(event) => setMembershipConfigForm((current) => ({
                            ...current,
                            backgroundCheckProvider: "checkr",
                            backgroundCheckWorkLocationId: event.target.value
                          }))}
                          placeholder="Checkr work location ID"
                        />
                      </label>
                    </div>
                    <label className="requirements-switch-row">
                      <input
                        type="checkbox"
                        checked={membershipConfigForm.backgroundCheckMockMode !== false}
                        onChange={(event) => setMembershipConfigForm((current) => ({
                          ...current,
                          backgroundCheckProvider: "checkr",
                          backgroundCheckMockMode: event.target.checked
                        }))}
                      />
                      <span>
                        <strong>Use test mode until Checkr production is ready</strong>
                        <small>Local and demo environments can test the member flow without sending a real report.</small>
                      </span>
                    </label>
                  </>
                ) : (
                  <div className="requirements-policy-strip">
                    <span className={membershipConfigForm.backgroundCheckRequired ? "is-required" : ""}>
                      {membershipConfigForm.backgroundCheckRequired ? "Required before activation" : "Not required"}
                    </span>
                    <span>
                      {membershipConfigForm.backgroundCheckRequired
                        ? `${membershipConfigForm.backgroundCheckPackageName || membershipConfigForm.backgroundCheckPackageId || "Checkr package not selected"} / ${membershipConfigForm.backgroundCheckEnvironment === "production" ? "Production" : "Test"}`
                        : "Checkr not enabled"}
                    </span>
                    {membershipConfigForm.backgroundCheckInstructions && (
                      <p className="requirements-guidance">
                        <strong>Staff guidance</strong>
                        {membershipConfigForm.backgroundCheckInstructions}
                      </p>
                    )}
                  </div>
                )}
                <div className="requirements-item-list">
                  <div className="requirements-item-list-header">
                    <strong>Screening setup</strong>
                    <span>{membershipConfigForm.backgroundCheckProvider === "checkr" || membershipConfigForm.backgroundCheckRequired ? "Checkr" : "Not connected"}</span>
                  </div>
                  <div className="requirements-item-row">
                    <div>
                      <strong>{membershipConfigForm.backgroundCheckPackageName || membershipConfigForm.backgroundCheckPackageId || "No screening package selected"}</strong>
                      <small>
                        {membershipConfigForm.backgroundCheckRequired
                          ? `${membershipConfigForm.backgroundCheckEnvironment === "production" ? "Production" : "Test"} mode${membershipConfigForm.backgroundCheckWorkLocationId ? ` / ${membershipConfigForm.backgroundCheckWorkLocationId}` : ""}`
                          : "Enable the gate and choose a package before using this step."}
                      </small>
                    </div>
                  </div>
                </div>
              </section>

            </div>
            )}

            {requirementsSubsection === "access" && (
            <section className="requirements-policy-card requirements-wide-card">
              <div className="requirements-policy-heading">
                <div>
                  <span>Access rules</span>
                  <h4>Qualification catalog</h4>
                  <p>Create reusable qualifications here, then assign each one to one or many real bays, lanes, classrooms, or areas below.</p>
                </div>
                <div className="requirements-card-actions">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => openRequirementBuilder("qualification")}
                  >
                    Add qualification
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequirementsEditTarget((current) => current === "access" ? "" : "access")}
                  >
                    {requirementsEditTarget === "access" ? "Done" : "Edit"}
                  </button>
                </div>
              </div>
              <div className="requirements-item-list requirements-qualification-list">
                <div className="requirements-item-list-header">
                  <strong>Active qualification keys</strong>
                  <span>{qualificationCatalogItems.length} configured</span>
                </div>
                {requirementsEditTarget === "access" && (
                  <div className="requirements-resource-filters">
                    <label className="requirements-resource-search">
                      Find resources
                      <input
                        value={accessResourceFilter}
                        onChange={(event) => setAccessResourceFilter(event.target.value)}
                        placeholder="Search by bay, lane, classroom, area..."
                      />
                    </label>
                    <label>
                      Resource type
                      <select
                        value={accessResourceTypeFilter}
                        onChange={(event) => setAccessResourceTypeFilter(event.target.value)}
                      >
                        <option value="">All types</option>
                        {assignableResourceTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}
                {qualificationCatalogItems.length > 0 ? (
                  qualificationCatalogItems.map((qualification, index) => {
                    const qualificationKey = normalizeQualificationKey(qualification.key || slugifyRequirementKey(qualification.label));
                    const appliedResources = qualificationAppliedResources(qualification);
                    return (
                      <div className="requirements-item-row requirements-qualification-row" key={qualification.id}>
                        <div>
                          <strong>{qualification.label}</strong>
                          <small>
                            Access requirement
                            {Number.parseInt(qualification.defaultExpiresInDays || "0", 10) > 0
                              ? ` / ${qualification.defaultExpiresInDays} days`
                              : " / no expiry"}
                            {qualification.description ? ` / ${qualification.description}` : ""}
                          </small>
                          <div className="requirements-resource-assignment">
                            <span>Applied to</span>
                            {appliedResources.length > 0 ? (
                              <strong>{appliedResources.map((resource) => resource.name || "Resource").join(", ")}</strong>
                            ) : (
                              <strong>No resources selected</strong>
                            )}
                            {requirementsEditTarget === "access" && (
                              <div className="requirements-resource-picker">
                                {resourcesEnabled && assignableResources.length > 0 ? (
                                  filteredAssignableResources.length > 0 ? filteredAssignableResources.map((resource) => {
                                    const selected = resourceQualificationKeys(resource).includes(qualificationKey);
                                    return (
                                      <label key={`${qualificationKey}-${resource.resourceId}`}>
                                        <input
                                          type="checkbox"
                                          checked={selected}
                                          disabled={updatingResourceId === resource.resourceId || !onUpdateResource}
                                          onChange={(event) => toggleResourceQualification(resource, qualification, event.target.checked)}
                                        />
                                        <span>
                                          <strong>{resource.name || "Resource"}</strong>
                                          <small>{resource.type || "resource"}</small>
                                        </span>
                                      </label>
                                    );
                                  }) : (
                                    <div className="requirements-item-empty">
                                      No resources match this search.
                                    </div>
                                  )
                                ) : (
                                  <div className="requirements-item-empty">
                                    Enable Range Facilities and create facilities before assigning this qualification.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {requirementsEditTarget === "access" && (
                          <button type="button" onClick={() => removeQualificationItem(index)}>
                            Remove
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="requirements-item-empty">
                    Use Add qualification to create an access key for resources, events, or classes.
                  </div>
                )}
              </div>
            </section>
            )}
            {(requirementsEditTarget || requirementsAddOpen) && (
              <div className="requirements-save-bar">
                <span>Changes are saved to the membership policy after review.</span>
                <button type="submit" disabled={savingMembershipConfig}>
                  {savingMembershipConfig ? "Saving..." : "Save requirements"}
                </button>
              </div>
            )}
          </form>
          </>
          )}

          {activeMembershipSection === "reviews" && (
          <>
          <div className="member-review-panel">
            <div className="panel-header">
              <h3>Qualification requests</h3>
              <p>Review member-submitted qualification requests before they unlock resource or event access.</p>
            </div>

            {pendingQualificationRequests.length === 0 ? (
              <div className="review-queue-status is-clear">
                <span>Clear</span>
                <strong>No pending qualification requests</strong>
                <small>Member-submitted requests will appear here when staff action is needed.</small>
              </div>
            ) : (
              <div className="member-qualification-request-list">
                {pendingQualificationRequests.map(({ member, qualification }) => (
                  <article className="member-qualification-request-card" key={qualification.qualificationId}>
                    <div className="qualification-request-main">
                      <div className="qualification-request-title">
                        <strong>{qualification.label}</strong>
                        <span>Member requested</span>
                      </div>
                      <p>{qualification.notes || "No member notes were submitted with this request."}</p>
                      <dl>
                        <div>
                          <dt>Member</dt>
                          <dd>{member.fullName || member.email || "Member record"}</dd>
                        </div>
                        <div>
                          <dt>Requested</dt>
                          <dd>{formatDateTime(qualification.requestedAt || qualification.createdAt)}</dd>
                        </div>
                        <div>
                          <dt>Access key</dt>
                          <dd>{qualification.key}</dd>
                        </div>
                      </dl>
                    </div>
                    <div className="qualification-request-actions">
                      <span>{member.email || shortReference(member.memberId)}</span>
                      <button
                        type="button"
                        disabled={memberDetailLoading && selectedMemberDetailId === member.memberId}
                        onClick={() => {
                          onOpenMemberDetail(member.memberId);
                          switchMembershipSection("members");
                        }}
                      >
                        Open member
                      </button>
                      <button
                        type="button"
                        className="primary-button"
                        disabled={addingMemberQualificationId === member.memberId}
                        onClick={() => onUpdateMemberQualification(member.memberId, qualification.qualificationId, {
                          status: "active",
                          reviewNotes: "Approved from qualification request queue"
                        })}
                      >
                        Approve access
                      </button>
                      <button
                        type="button"
                        className="secondary-button"
                        disabled={addingMemberQualificationId === member.memberId}
                        onClick={() => onUpdateMemberQualification(member.memberId, qualification.qualificationId, {
                          status: "revoked",
                          reviewNotes: "Rejected from qualification request queue"
                        })}
                      >
                        Reject
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>

          <div className="member-review-panel">
            <div className="panel-header">
              <h3>Applicant review queue</h3>
              <p>Track paid public signups from payment through waiver, qualification, and final approval.</p>
            </div>

            <div className="review-workbench-note">
              <strong>Review workbench</strong>
              <span>
                This queue is for deciding whether an applicant is ready. Use the member roster for the full member record,
                profile edits, plan operations, and long-term history.
              </span>
            </div>

            <div className="applicant-queue-toolbar">
              <label>
                Filter
                <select value={applicantFilter} onChange={(event) => setApplicantFilter(event.target.value)}>
                  {APPLICANT_FILTERS.map((filter) => (
                    <option key={filter.value} value={filter.value}>{filter.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Search
                <input
                  value={applicantSearch}
                  onChange={(event) => setApplicantSearch(event.target.value)}
                  placeholder="Name, email, applicant note"
                />
              </label>
              <div className="applicant-queue-counts">
                <span>{applicantMembers.length} total</span>
                <span>{filteredApplicants.length} shown</span>
                <span>{applicantMembers.filter(applicantReady).length} ready</span>
              </div>
              <button
                type="button"
                className="secondary-button"
                disabled={filteredApplicants.length === 0}
                onClick={exportApplicantsCsv}
              >
                Export review records
              </button>
            </div>

            {membersLoading && (
              <div className="review-queue-status">
                <span>Syncing</span>
                <strong>Loading applicant queue</strong>
              </div>
            )}
            {!membersLoading && applicantMembers.length === 0 && (
              <div className="review-queue-status is-clear">
                <span>Clear</span>
                <strong>No public membership applicants</strong>
                <small>Paid public signups will land here for waiver, qualification, and approval review.</small>
              </div>
            )}
            {!membersLoading && applicantMembers.length > 0 && filteredApplicants.length === 0 && (
              <div className="review-queue-status">
                <span>Filtered</span>
                <strong>No applicants match this view</strong>
                <small>Adjust the filter or search term to widen the queue.</small>
              </div>
            )}
            {!membersLoading && filteredApplicants.length > 0 && (
              <div className="member-review-list">
                {filteredApplicants.map((member) => {
                  const planChangeRequest = member.planChangeRequest?.status === "pending"
                    ? member.planChangeRequest
                    : null;
                  const currentType = membershipTypes.find((item) => item.membershipTypeId === member.membershipTypeId);
                  const requestedTypeId = planChangeRequest?.toMembershipTypeId || member.membershipTypeId;
                  const type = membershipTypes.find((item) => item.membershipTypeId === requestedTypeId);
                  const busy = updatingMemberId === member.memberId;
                  const requirements = member.review?.requirements || {};
                  const ready = applicantReady(member);
                  const draft = reviewDraft(member.memberId);
                  const waiverRequired = requirementRequired(member, "waiver");
                  const waiverStatus = waiverRequired ? requirementStatus(member, "waiver") : "not_required";
                  const qualificationRequired = requirementRequired(member, "prequalification");
                  const qualificationStatus = qualificationRequired ? requirementStatus(member, "prequalification") : "not_required";
                  const backgroundRequired = requirementRequired(member, "backgroundCheck");
                  const backgroundStatus = backgroundRequired ? requirementStatus(member, "backgroundCheck") : "not_required";
                  const backgroundDecisionStatus = requirements.backgroundCheck?.backgroundCheckDecision?.status;
                  const checkr = requirements.backgroundCheck?.checkr || {};
                  const balanceDueCents = Number(member.membership?.balanceDueCents || 0);
                  const balancePaymentStatus = member.membership?.balancePaymentStatus || member.stripe?.balancePaymentStatus || "";
                  const balanceDueAt = member.membership?.balanceDueAt;
                  const balanceExpired = Boolean(
                    balanceDueAt &&
                    Date.parse(balanceDueAt) <= Date.now() &&
                    balancePaymentStatus !== "paid"
                  );
                  const awaitingPrescreenBalance = Boolean(
                    !planChangeRequest &&
                    member.review?.status === "approved" &&
                    member.status === "pending" &&
                    balanceDueCents > 0 &&
                    balancePaymentStatus !== "paid"
                  );
	                  const stepsCompleted = member.reviewSummary?.prequalificationStepsCompleted
	                    ?? requirements.prequalification?.checklist?.filter((step) => step.status === "completed").length
	                    ?? 0;
	                  const stepsTotal = member.reviewSummary?.prequalificationStepsTotal
	                    ?? requirements.prequalification?.checklist?.length
	                    ?? 0;
	                  const reviewApplicationForm = normalizeApplicationFormForClient(requirements.prequalification?.applicationForm);
	                  const reviewApplicationResponses = requirements.prequalification?.applicationResponses || [];
	                  const reviewApplicationSections = applicationFormSectionsWithResponses(reviewApplicationForm, reviewApplicationResponses);
	                  const hasApplicationResponses = reviewApplicationResponses.length > 0;
                  const missingItems = [
                    requirementOpen(member, "waiver") ? "waiver" : null,
                    requirementOpen(member, "prequalification") ? "onboarding" : null,
                    checklistOpen(member, "prequalification") ? "onboarding steps" : null,
                    requirementOpen(member, "backgroundCheck") ? "background check" : null,
                    checklistOpen(member, "backgroundCheck") ? "background-check steps" : null
                  ].filter(Boolean);
                  const pendingDecision = Boolean(planChangeRequest) || (member.status === "pending" && !awaitingPrescreenBalance);
                  const decisionLabel = pendingDecision
                    ? planChangeRequest
                      ? "Plan change waiting for approval"
                      : ready
                        ? "Ready for decision"
                        : `${missingItems.length || 1} item${missingItems.length === 1 ? "" : "s"} blocking acceptance`
                    : awaitingPrescreenBalance
                      ? balanceExpired
                        ? "Balance window expired"
                        : "Awaiting remaining balance"
                    : member.review?.status === "approved" || member.status === "active"
                      ? "Accepted"
                      : member.review?.status || member.status;
                  const requestedActionLabel = pendingDecision
                    ? planChangeRequest
                      ? "Approve or reject plan change"
                      : member.review?.status === "needs_info"
                        ? "Waiting on applicant info"
                      : ready
                        ? "Accept or reject applicant"
                        : missingItems.length > 0
                          ? `Resolve ${missingItems.join(", ")}`
                          : "Finish review requirements"
                    : awaitingPrescreenBalance
                      ? balanceExpired
                        ? "Reopen balance window"
                        : "Member pays remaining balance"
                    : "Open member record";
                  const onboardingStatusLabel = stepsTotal > 0
                    ? `${stepsCompleted}/${stepsTotal} steps complete`
                    : reviewStatusCopy(qualificationStatus, "Not required");
                  const reviewBadgeLabel = pendingDecision
                    ? planChangeRequest
                      ? "Plan change"
                      : member.review?.status === "needs_info"
                        ? "Needs info"
                      : ready
                        ? "Ready"
                        : "Blocked"
                    : member.review?.status === "approved" || member.status === "active"
                      ? "Accepted"
                      : (member.review?.status || member.status || "Recorded");
                  const paymentLabel = planChangeRequest?.paymentStatus || member.stripe?.paymentStatus || member.stripe?.subscriptionStatus || "No payment record";
                  const signupSourceLabel = planChangeRequest ? "Member plan change" : (member.campaign?.name || member.campaign?.code || reviewStatusCopy(member.source, "Public signup"));
                  return (
                    <article className={`member-review-card ${ready ? "is-ready" : ""}`} key={member.memberId}>
                      <div className="review-applicant-summary">
                        <div className="review-card-title">
                          <strong>{member.fullName}</strong>
                          <span className={`status-badge ${ready ? "is-ready" : pendingDecision ? "is-blocked" : ""}`}>{reviewBadgeLabel}</span>
                        </div>
                        <dl className="review-applicant-facts">
                          <div>
                            <dt>Contact</dt>
                            <dd>{member.email || "No email"}</dd>
                          </div>
                          <div>
                            <dt>Plan</dt>
                            <dd>
                              {planChangeRequest
                                ? `${currentType?.name || membershipTypeName(planChangeRequest.fromMembershipTypeId, "Current plan needs review")} -> ${type?.name || membershipTypeName(planChangeRequest.toMembershipTypeId, "Requested plan needs review")}`
                                : type?.name || membershipTypeName(member.membershipTypeId)}
                            </dd>
                          </div>
                          <div>
                            <dt>Payment</dt>
                            <dd>
                              {balanceDueCents > 0
                                ? `${reviewStatusCopy(balancePaymentStatus || paymentLabel, "No payment record")} / ${formatCurrency(balanceDueCents, type?.pricing?.currency || "usd")}`
                                : reviewStatusCopy(paymentLabel, "No payment record")}
                            </dd>
                          </div>
                          <div>
                            <dt>Signup source</dt>
                            <dd>{signupSourceLabel}</dd>
                          </div>
                          <div>
                            <dt>Waiver</dt>
                            <dd>{waiverRequired ? reviewStatusCopy(waiverStatus) : "Not required"}</dd>
                          </div>
                          <div>
                            <dt>Onboarding</dt>
                            <dd>{onboardingStatusLabel}</dd>
                          </div>
                          <div>
                            <dt>Qualification</dt>
                            <dd>{qualificationRequired ? reviewStatusCopy(qualificationStatus) : "Not required"}</dd>
                          </div>
                          <div>
                            <dt>Background</dt>
                            <dd>{backgroundRequired ? reviewStatusCopy(backgroundStatus) : "Not required"} </dd>
                          </div>
                          <div>
                            <dt>Next action</dt>
                            <dd>{requestedActionLabel}</dd>
                          </div>
                        </dl>
                        <details className="review-audit-details">
                          <summary>Audit details</summary>
                          <dl>
                            {member.signupIntentId && (
                              <div>
                                <dt>Signup</dt>
                                <dd>{shortReference(member.signupIntentId)}</dd>
                              </div>
                            )}
                            {(member.source || member.campaign) && (
                              <div>
                                <dt>Attribution</dt>
                                <dd>
                                  {[member.source, member.campaign?.name || member.campaign?.code, member.campaign?.medium]
                                    .filter(Boolean)
                                    .join(" / ")}
                                </dd>
                              </div>
                            )}
                            {member.stripe?.subscriptionStatus && (
                              <div>
                                <dt>Subscription</dt>
                                <dd>{member.stripe.subscriptionStatus}</dd>
                              </div>
                            )}
                            {member.expiration?.currentTermEnd && (
                              <div>
                                <dt>Term</dt>
                                <dd>{formatExpiration(member)}</dd>
                              </div>
                            )}
                            {member.review?.reason && (
                              <div>
                                <dt>Reason</dt>
                                <dd>{member.review.reason}</dd>
                              </div>
                            )}
                          </dl>
                        </details>
                      </div>
                      <div className="review-requirements">
                        <div className="review-requirement-chip-row">
                          <span className={`review-requirement-chip is-${waiverStatus.replace(/_/g, "-")}`}>
                            Waiver
                            <strong>{waiverRequired ? waiverStatus.replace(/_/g, " ") : "not required"}</strong>
                          </span>
                          <span className={`review-requirement-chip is-${qualificationStatus.replace(/_/g, "-")}`}>
                            Qualification
                            <strong>{qualificationRequired ? qualificationStatus.replace(/_/g, " ") : "not required"}</strong>
                          </span>
                          {stepsTotal > 0 && (
                            <span className={`review-requirement-chip ${stepsCompleted >= stepsTotal ? "is-completed" : "is-pending"}`}>
                              Checklist
                              <strong>{stepsCompleted}/{stepsTotal} complete</strong>
                            </span>
                          )}
                          {backgroundRequired && (
                            <span className={`review-requirement-chip is-${(backgroundDecisionStatus || backgroundStatus).replace(/_/g, "-")}`}>
                              Background
                              <strong>{reviewStatusCopy(backgroundDecisionStatus || backgroundStatus)}</strong>
                            </span>
                          )}
                        </div>
	                        <div className="review-requirement-detail-list">
                          {requirements.waiver?.document?.fileName && (
                            <span>
                              Waiver file: {requirements.waiver.document.fileName}
                              {requirements.waiver.document.versionLabel ? ` (${requirements.waiver.document.versionLabel})` : ""}
                            </span>
                          )}
                          {requirements.waiver?.applicantAcknowledgementExpiresAt && (
                            <span>Waiver expires: {formatDateOnly(requirements.waiver.applicantAcknowledgementExpiresAt)}</span>
                          )}
                          {member.reviewSummary?.prequalificationDecisionStatus && (
                            <span>
                              Qualification decision: {member.reviewSummary.prequalificationDecisionStatus.replace("_", " ")}
                              {member.reviewSummary.prequalificationDecisionReason
                                ? ` / ${member.reviewSummary.prequalificationDecisionReason}`
                                : ""}
                            </span>
                          )}
                          {backgroundDecisionStatus && (
                            <span>
                              Background decision: {reviewStatusCopy(backgroundDecisionStatus)}
                              {requirements.backgroundCheck?.backgroundCheckDecision?.reason
                                ? ` / ${requirements.backgroundCheck.backgroundCheckDecision.reason}`
                                : ""}
                            </span>
	                          )}
                          {requirements.backgroundCheck?.provider === "checkr" && (
                            <span>
                              Checkr: {checkr.packageName || checkr.packageId || "Package not set"} / {(checkr.status || "not started").replace(/_/g, " ")}
                              {checkr.result && checkr.result !== "pending" ? ` / ${checkr.result}` : ""}
                              {checkr.submittedAt ? ` / submitted ${formatDateOnly(checkr.submittedAt)}` : ""}
                            </span>
                          )}
	                        </div>
                          {requirements.backgroundCheck?.provider === "checkr" && (
                            <details className="review-audit-details">
                              <summary>Background-check technical details</summary>
                              <dl>
                                <div>
                                  <dt>Provider</dt>
                                  <dd>Checkr</dd>
                                </div>
                                <div>
                                  <dt>Screening</dt>
                                  <dd>{checkr.packageName || checkr.packageId || "Not selected"}</dd>
                                </div>
                                <div>
                                  <dt>Provider status</dt>
                                  <dd>{(checkr.status || "not started").replace(/_/g, " ")}</dd>
                                </div>
                                {checkr.eta && (
                                  <div>
                                    <dt>Estimated completion</dt>
                                    <dd>{formatDateOnly(checkr.eta)}</dd>
                                  </div>
                                )}
                                {(checkr.reportId || checkr.candidateId) && (
                                  <div>
                                    <dt>References</dt>
                                    <dd>{[checkr.reportId, checkr.candidateId].filter(Boolean).map(shortReference).join(" / ")}</dd>
                                  </div>
                                )}
                              </dl>
                            </details>
                          )}
	                        {hasApplicationResponses && (
	                          <div className="review-application-answers">
	                            <div className="review-application-answers-header">
	                              <strong>{reviewApplicationForm.title || "Membership application"}</strong>
	                              <span>
	                                Submitted {formatDateOnly(requirements.prequalification?.applicationSubmittedAt)}
	                              </span>
	                            </div>
	                            {reviewApplicationSections.map((section) => (
	                              <section className="review-application-section" key={section.sectionId}>
	                                <h4>{section.title}</h4>
	                                <dl>
	                                  {(section.questions || []).map((question) => (
	                                    <div key={question.questionId}>
	                                      <dt>{question.label}</dt>
	                                      <dd>{applicationAnswerDisplay(question.response) || "No response"}</dd>
	                                    </div>
	                                  ))}
	                                </dl>
	                              </section>
	                            ))}
	                          </div>
	                        )}
	                        {requirements.prequalification?.checklist?.length > 0 && (
                          <div className="review-checklist">
                            {requirements.prequalification.checklist.map((step) => (
                              <div className="review-checklist-item" key={step.key}>
                                <button
                                  type="button"
                                  disabled={busy || step.status === "completed"}
                                  onClick={() => onUpdateMemberReview(member.memberId, buildChecklistStepPatch(member, "prequalification", step.key))}
                                >
                                  {step.status === "completed" ? "Done: " : "Mark done: "}{step.label}
                                </button>
                                {step.applicantResponse && (
                                  <p className="review-response">
                                    {step.applicantResponse}
                                    {step.applicantSubmittedAt
                                      ? ` Submitted ${new Date(step.applicantSubmittedAt).toLocaleDateString()}.`
                                      : ""}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="review-decision-panel">
                        <span>Decision</span>
                        <strong>{decisionLabel}</strong>
                        {pendingDecision && !ready && !planChangeRequest && (
                          <p>Missing: {missingItems.length > 0 ? missingItems.join(", ") : "review requirements"}</p>
                        )}
                        {awaitingPrescreenBalance && (
                          <p>
                            {balanceExpired
                              ? `The remaining balance was due ${formatDateOnly(balanceDueAt)}. Reopen the window when the applicant is allowed to pay.`
                              : `Remaining balance is due ${balanceDueAt ? formatDateOnly(balanceDueAt) : "after approval"}.`}
                          </p>
                        )}
                        {!pendingDecision && !awaitingPrescreenBalance && (
                          <p>This applicant is no longer waiting for review. Open the member record for ongoing operations.</p>
                        )}
                        <div className="review-decision-actions">
                          {awaitingPrescreenBalance && balanceExpired && (
                            <button
                              type="button"
                              className="review-primary-action"
                              disabled={busy}
                              onClick={() => onUpdateMemberReview(member.memberId, {
                                reopenBalanceWindow: true,
                                notes: draft.notes.trim() || undefined,
                                reason: draft.reason.trim() || "Balance payment window reopened"
                              })}
                            >
                              Reopen payment window
                            </button>
                          )}
                          {pendingDecision && (
                            <button
                              type="button"
                              className="review-primary-action"
                              disabled={busy || !ready}
                              onClick={() => onUpdateMemberReview(member.memberId, {
                                decision: "approve",
                                notes: draft.notes.trim() || undefined,
                                reason: draft.reason.trim() || undefined
                              })}
                            >
                              {planChangeRequest ? "Approve change" : "Accept applicant"}
                            </button>
                          )}
                          {pendingDecision && (
                            <button
                              type="button"
                              className="danger-button"
                              disabled={busy}
                              onClick={() => onUpdateMemberReview(member.memberId, {
                                decision: "reject",
                                notes: draft.notes.trim() || undefined,
                                reason: draft.reason.trim() || "Rejected by range review"
                              })}
                            >
                              {planChangeRequest ? "Reject change" : "Reject"}
                            </button>
                          )}
                          {pendingDecision && !planChangeRequest && (
                            <button
                              type="button"
                              className="secondary-button"
                              disabled={busy}
                              onClick={() => onUpdateMemberReview(member.memberId, buildRequestInfoPatch(draft))}
                            >
                              Request info
                            </button>
                          )}
                        </div>
                      </div>
                      <details className="review-staff-tools">
                        <summary>
                          <span>Staff tools</span>
                          <strong>Notes, records, and requirement corrections</strong>
                        </summary>
                        <div className="review-note-editor">
                          <label>
                            Staff note
                            <textarea
                              value={draft.notes}
                              onChange={(event) => updateReviewDraft(member.memberId, { notes: event.target.value })}
                              placeholder={member.notes || "Add an internal review note"}
                              rows="2"
                            />
                          </label>
                          <label>
                            Decision reason
                            <input
                              value={draft.reason}
                              onChange={(event) => updateReviewDraft(member.memberId, { reason: event.target.value })}
                              placeholder={member.review?.reason || "Reason shown in review history"}
                            />
                          </label>
                        </div>
                        <div className="row-actions review-actions">
                          <button
                            type="button"
                            disabled={memberDetailLoading && selectedMemberDetailId === member.memberId}
                            onClick={() => {
                              onOpenMemberDetail(member.memberId);
                              switchMembershipSection("members");
                            }}
                          >
                            Open member record
                          </button>
                          <button
                            type="button"
                            disabled={busy || !draft.notes.trim()}
                            onClick={() => onUpdateMemberReview(member.memberId, { notes: draft.notes.trim() })}
                          >
                            Save note
                          </button>
                          {requirements.waiver?.required !== false && requirements.waiver?.status !== "completed" && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => onUpdateMemberReview(member.memberId, buildRequirementPatch(member, "waiver", "completed"))}
                            >
                              Mark waiver done
                            </button>
                          )}
                          {requirements.prequalification?.required && requirements.prequalification?.status !== "completed" && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => onUpdateMemberReview(member.memberId, buildRequirementPatch(member, "prequalification", "completed"))}
                            >
                              Mark qualification done
                            </button>
                          )}
                          {requirements.prequalification?.required && requirements.prequalification?.status !== "completed" && (
                            <>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => onUpdateMemberReview(member.memberId, buildPrequalificationDecisionPatch("approved", draft))}
                              >
                                Approve qualification
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => onUpdateMemberReview(member.memberId, buildPrequalificationDecisionPatch("rejected", draft))}
                              >
                                Reject qualification
                              </button>
                            </>
                          )}
                          {requirements.backgroundCheck?.required && requirements.backgroundCheck?.status !== "completed" && (
                            <>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => onUpdateMemberReview(member.memberId, buildBackgroundCheckDecisionPatch("clear", draft))}
                              >
                                Clear background
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => onUpdateMemberReview(member.memberId, buildBackgroundCheckDecisionPatch("needs_info", draft))}
                              >
                                Background follow-up
                              </button>
                              <button
                                type="button"
                                className="danger-button"
                                disabled={busy}
                                onClick={() => onUpdateMemberReview(member.memberId, buildBackgroundCheckDecisionPatch("failed", draft, { decision: "reject" }))}
                              >
                                Fail background - no refund
                              </button>
                            </>
                          )}
                        </div>
                      </details>
                    </article>
                  );
                })}
              </div>
            )}
          </div>

          </>
          )}

          {activeMembershipSection === "plans" && (
          <>
          {!stripeReady && (
          <div className="membership-readiness-strip">
            <div>
              <span>Payment readiness</span>
              <strong>{paymentsLoading ? "Checking..." : stripeStatusLabel}</strong>
            </div>
            <p>
              Paid public plans can be published only after this range has a ready Stripe account.
              {stripePayment.lastSyncedAt ? ` Last checked ${new Date(stripePayment.lastSyncedAt).toLocaleString()}.` : ""}
            </p>
            <div className="row-actions">
              <button
                type="button"
                disabled={startingStripeConnect || paymentSettings?.configured === false}
                onClick={onStartStripeConnect}
              >
                {stripePayment.connectedAccountId ? "Resume Stripe onboarding" : "Connect Stripe"}
              </button>
              <button
                type="button"
                className="secondary-button"
                disabled={refreshingStripeConnect || paymentsLoading || !stripePayment.connectedAccountId}
                onClick={onRefreshStripeConnect}
              >
                {refreshingStripeConnect ? "Refreshing..." : "Refresh status"}
              </button>
            </div>
          </div>
          )}

          <div className="membership-plan-command-bar">
            <div>
              <h3>Plan catalog</h3>
              <p>Use this list for the plans a range owner sells publicly or assigns manually.</p>
            </div>
            {planEditorOpen ? (
              <span className="membership-plan-command-status">Editor open</span>
            ) : (
              <button
                type="button"
                onClick={() => {
                  onResetMembershipTypeForm();
                  setPlanEditorExpanded(true);
                }}
              >
                Add membership plan
              </button>
            )}
          </div>

          {planEditorOpen && (
          <div className="membership-plan-editor">
              <form className="member-form" onSubmit={onSaveMembershipType}>
                <div className="panel-header membership-plan-editor-header">
                  <div>
                    <h3>{editingMembershipTypeId ? "Edit membership plan" : "Add membership plan"}</h3>
                    <p>Set the name, price, term, and reservation benefits. Public paid plans require Stripe readiness.</p>
                  </div>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => {
                      onResetMembershipTypeForm();
                      setPlanEditorExpanded(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>

                <div className="membership-plan-form-section">
                  <div className="membership-plan-form-heading">
                    <h4>Plan details</h4>
                    <p>Name the plan in the same language members will see.</p>
                  </div>
                  <label>
                    Plan name
                    <input
                      value={membershipTypeForm.name}
                      onChange={(event) => setMembershipTypeForm((current) => ({ ...current, name: event.target.value }))}
                      placeholder="Annual individual"
                      required
                    />
                  </label>

                  <label>
                    Description
                    <input
                      value={membershipTypeForm.description}
                      onChange={(event) => setMembershipTypeForm((current) => ({ ...current, description: event.target.value }))}
                      placeholder="Annual range access with reservation benefits"
                    />
                  </label>
                </div>

                <div className="membership-plan-form-section">
                  <div className="membership-plan-form-heading">
                    <h4>Billing and term</h4>
                    <p>Control how members pay and how long access lasts.</p>
                  </div>
                  <div className="form-row">
                    <label>
                      Plan category
                      <select
                        value={membershipTypeForm.category}
                        onChange={(event) => setMembershipTypeForm((current) => ({
                          ...current,
                          category: event.target.value
                        }))}
                      >
                        <option value="membership">Individual membership</option>
                        <option value="family">Family membership</option>
                      </select>
                    </label>
                    <label>
                      Billing model
                      <select
                        value={membershipTypeForm.billingModel}
                        onChange={(event) => setMembershipTypeForm((current) => ({
                          ...current,
                          billingModel: event.target.value,
                          termUnit: event.target.value === "one_time" && current.termUnit === "lifetime" ? "lifetime" : current.termUnit
                        }))}
                      >
                        <option value="subscription">Subscription</option>
                        <option value="one_time">One-time payment</option>
                      </select>
                    </label>
                    <label>
                      Billing interval
                      <select
                        value={membershipTypeForm.billingInterval}
                        disabled={membershipTypeForm.billingModel === "one_time"}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, billingInterval: event.target.value }))}
                      >
                        <option value="month">Monthly</option>
                        <option value="year">Annual</option>
                      </select>
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      Term count
                      <input
                        type="number"
                        min="1"
                        disabled={membershipTypeForm.termUnit === "lifetime"}
                        value={membershipTypeForm.termCount}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, termCount: event.target.value }))}
                        required
                      />
                    </label>
                    <label>
                      Term unit
                      <select
                        value={membershipTypeForm.termUnit}
                        onChange={(event) => setMembershipTypeForm((current) => ({
                          ...current,
                          termUnit: event.target.value,
                          termCount: event.target.value === "lifetime" ? "1" : current.termCount,
                          billingModel: event.target.value === "lifetime" ? "one_time" : current.billingModel
                        }))}
                      >
                        <option value="month">Month</option>
                        <option value="year">Year</option>
                        <option value="lifetime">Lifetime</option>
                      </select>
                    </label>
                    <label>
                      Price
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={membershipTypeForm.price}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, price: event.target.value }))}
                        required
                      />
                    </label>
                    <label>
                      Status
                      <select
                        value={membershipTypeForm.status}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, status: event.target.value }))}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div className="membership-plan-form-section">
                  <div className="membership-plan-form-heading">
                    <h4>Payment flow</h4>
                    <p>Choose whether members pay the full amount up front or start with a screening fee before owner approval.</p>
                  </div>
                  <div className="form-row">
                    <label>
                      Checkout behavior
                      <select
                        value={membershipTypeForm.paymentMode}
                        onChange={(event) => setMembershipTypeForm((current) => ({
                          ...current,
                          paymentMode: event.target.value
                        }))}
                      >
                        <option value="full_payment">Full membership payment</option>
                        <option value="prescreen_then_balance">Prescreening fee, then balance</option>
                      </select>
                    </label>
                    {membershipTypeForm.paymentMode === "prescreen_then_balance" && (
                      <>
                        <label>
                          Prescreening fee
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={membershipTypeForm.prescreeningFee}
                            onChange={(event) => setMembershipTypeForm((current) => ({ ...current, prescreeningFee: event.target.value }))}
                            placeholder="100.00"
                          />
                        </label>
                        <label>
                          Balance due after approval
                          <input
                            type="number"
                            min="1"
                            max="120"
                            value={membershipTypeForm.balanceDueDays}
                            onChange={(event) => setMembershipTypeForm((current) => ({ ...current, balanceDueDays: event.target.value }))}
                          />
                        </label>
                      </>
                    )}
                  </div>
                  {membershipTypeForm.paymentMode === "prescreen_then_balance" && (
                    <>
                      <label>
                        Fee label shown at checkout
                        <input
                          value={membershipTypeForm.prescreeningLabel}
                          onChange={(event) => setMembershipTypeForm((current) => ({ ...current, prescreeningLabel: event.target.value }))}
                          placeholder="Background prescreening fee"
                        />
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          aria-label="Prescreening fee is non-refundable if screening fails"
                          checked={membershipTypeForm.prescreeningNonRefundable}
                          onChange={(event) => setMembershipTypeForm((current) => ({ ...current, prescreeningNonRefundable: event.target.checked }))}
                        />
                        Prescreening fee is non-refundable if screening fails
                      </label>
                    </>
                  )}
                </div>

                <div className="membership-plan-form-section">
                  <div className="membership-plan-form-heading">
                    <h4>Publishing</h4>
                    <p>Decide whether this plan is owner-managed or visible for future online signup.</p>
                  </div>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      aria-label="Show on future public signup page"
                      checked={membershipTypeForm.publicSignupEnabled}
                      onChange={(event) => setMembershipTypeForm((current) => ({ ...current, publicSignupEnabled: event.target.checked }))}
                    />
                    Show on future public signup page
                  </label>

                  {chaptersEnabled && (
                    <div className="chapter-scope-editor">
                      <div>
                        <strong>Chapter availability</strong>
                        <span>{chaptersLoading ? "Loading chapters..." : "Limit this plan to specific chapters when pricing or eligibility differs."}</span>
                      </div>
                      <label>
                        Availability
                        <select
                          value={membershipTypeForm.chapterScopeMode || "all"}
                          onChange={(event) => setMembershipTypeForm((current) => ({ ...current, chapterScopeMode: event.target.value }))}
                        >
                          <option value="all">All chapters</option>
                          <option value="selected">Selected chapters only</option>
                        </select>
                      </label>
                      {membershipTypeForm.chapterScopeMode === "selected" && (
                        <div className="chapter-checkbox-list">
                          {chapters.map((chapter) => {
                            const currentIds = new Set(String(membershipTypeForm.chapterScopeIds || "").split(/\n|,/).map((item) => item.trim()).filter(Boolean));
                            const checked = currentIds.has(chapter.chapterId);
                            return (
                              <label key={chapter.chapterId} className="chapter-checkbox-row">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(event) => {
                                    const nextIds = new Set(currentIds);
                                    if (event.target.checked) {
                                      nextIds.add(chapter.chapterId);
                                    } else {
                                      nextIds.delete(chapter.chapterId);
                                    }
                                    setMembershipTypeForm((current) => ({
                                      ...current,
                                      chapterScopeIds: [...nextIds].join("\n")
                                    }));
                                  }}
                                />
                                <span>
                                  <strong>{chapter.name}</strong>
                                  <small>{[chapter.city, chapter.region].filter(Boolean).join(", ") || "Chapter"}</small>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                      <label>
                        Default chapter for manual adds
                        <select
                          value={membershipTypeForm.primaryChapterId || ""}
                          onChange={(event) => setMembershipTypeForm((current) => ({ ...current, primaryChapterId: event.target.value }))}
                        >
                          <option value="">No default chapter</option>
                          {chapters.map((chapter) => (
                            <option key={chapter.chapterId} value={chapter.chapterId}>
                              {chapterLabel(chapter)}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}

                  <TaskDisclosure
                    actionLabel="Configure"
                    defaultOpen={planCampaignEditorOpen}
                    key={`plan-campaign-${editingMembershipTypeId || "new"}-${planCampaignEditorOpen ? "open" : "closed"}`}
                    title="Limited presale campaign"
                    description="Cap a launch offer such as Liberty's first 100 memberships and decide whether members see remaining spots."
                  >
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        aria-label="Limit the number of public signups for this plan"
                        checked={membershipTypeForm.campaignEnabled}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, campaignEnabled: event.target.checked }))}
                      />
                      Limit the number of public signups for this plan
                    </label>
                    <div className="form-row">
                      <label>
                        Campaign label
                        <input
                          value={membershipTypeForm.campaignLabel}
                          onChange={(event) => setMembershipTypeForm((current) => ({ ...current, campaignLabel: event.target.value }))}
                          placeholder="First 100 presale"
                        />
                      </label>
                      <label>
                        Signup limit
                        <input
                          type="number"
                          min="0"
                          value={membershipTypeForm.campaignMaxSignups}
                          onChange={(event) => setMembershipTypeForm((current) => ({ ...current, campaignMaxSignups: event.target.value }))}
                          placeholder="100"
                        />
                      </label>
                    </div>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        aria-label="Show remaining spots on the public page"
                        checked={membershipTypeForm.campaignShowRemaining}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, campaignShowRemaining: event.target.checked }))}
                      />
                      Show remaining spots on the public page
                    </label>
                  </TaskDisclosure>

                  {paidPublicPlanBlocked && (
                    <div className="notice is-error">
                      <span>Connect Stripe payments before publishing a paid public membership plan.</span>
                      <button
                        type="button"
                        className="secondary-button"
                        disabled={startingStripeConnect || paymentSettings?.configured === false}
                        onClick={onStartStripeConnect}
                      >
                        {stripePayment.connectedAccountId ? "Resume Stripe onboarding" : "Connect Stripe"}
                      </button>
                    </div>
                  )}
                </div>

                <div className="membership-plan-form-section">
                  <div className="membership-plan-form-heading">
                    <h4>Benefits and access rules</h4>
                    <p>Keep common plan benefits visible and tuck advanced rules behind one control.</p>
                  </div>
                  <label>
                    Benefit notes
                    <input
                      value={membershipTypeForm.benefitsNotes}
                      onChange={(event) => setMembershipTypeForm((current) => ({ ...current, benefitsNotes: event.target.value }))}
                      placeholder="Includes member pricing, priority reservations, and range updates"
                    />
                  </label>
                  <label>
                    Member-facing benefit list
                    <textarea
                      value={membershipTypeForm.benefitsItemsText}
                      onChange={(event) => setMembershipTypeForm((current) => ({ ...current, benefitsItemsText: event.target.value }))}
                      placeholder={"One benefit per line\nOrientation and range rules review\nMember portal for reservations and payments"}
                      rows={4}
                    />
                  </label>
                  <label>
                    Required qualification keys
                    <textarea
                      value={membershipTypeForm.requiredQualificationKeysText}
                      onChange={(event) => setMembershipTypeForm((current) => ({ ...current, requiredQualificationKeysText: event.target.value }))}
                      placeholder={"One qualification key per line\n1000-yard-qualified"}
                      rows={3}
                    />
                  </label>

                  <TaskDisclosure
                    actionLabel="Configure"
                    defaultOpen={planBenefitsEditorOpen}
                    key={`plan-benefits-${editingMembershipTypeId || "new"}-${planBenefitsEditorOpen ? "open" : "closed"}`}
                    title="Reservation and guest benefits"
                    description="Optional rules for guest passes, lane reservations, and members-only events."
                  >
                  <div className="form-row">
                    <label>
                      Guest passes
                      <input
                        type="number"
                        min="0"
                        value={membershipTypeForm.guestPasses}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, guestPasses: event.target.value }))}
                      />
                    </label>
                    <label>
                      Guest pass period
                      <select
                        value={membershipTypeForm.guestPassPeriod}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, guestPassPeriod: event.target.value }))}
                      >
                        <option value="month">Per month</option>
                        <option value="year">Per year</option>
                        <option value="term">Per term</option>
                      </select>
                    </label>
                  </div>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      aria-label="Guest passes roll over"
                      checked={membershipTypeForm.guestPassRollover}
                      onChange={(event) => setMembershipTypeForm((current) => ({ ...current, guestPassRollover: event.target.checked }))}
                    />
                    Guest passes roll over
                  </label>

                  <div className="plan-rules">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        aria-label="Allows lane reservations"
                        checked={membershipTypeForm.laneReservationAllowed}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, laneReservationAllowed: event.target.checked }))}
                      />
                      Allows lane reservations
                    </label>
                    <div className="form-row">
                      <label>
                        Advance booking days
                        <input
                          type="number"
                          min="0"
                          aria-label="Advance booking days"
                          value={membershipTypeForm.advanceBookingDays}
                          onChange={(event) => setMembershipTypeForm((current) => ({ ...current, advanceBookingDays: event.target.value }))}
                        />
                      </label>
                      <label>
                        Included lane hours / month
                        <input
                          type="number"
                          min="0"
                          aria-label="Included lane hours per month"
                          value={membershipTypeForm.includedHoursPerMonth}
                          onChange={(event) => setMembershipTypeForm((current) => ({ ...current, includedHoursPerMonth: event.target.value }))}
                        />
                      </label>
                      <label>
                        Max active reservations
                        <input
                          type="number"
                          min="0"
                          aria-label="Max active reservations"
                          value={membershipTypeForm.maxActiveReservations}
                          onChange={(event) => setMembershipTypeForm((current) => ({ ...current, maxActiveReservations: event.target.value }))}
                        />
                      </label>
                    </div>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        aria-label="Allows members-only events"
                        checked={membershipTypeForm.membersOnlyEventsAllowed}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, membersOnlyEventsAllowed: event.target.checked }))}
                      />
                      Allows members-only events
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        aria-label="Allows rental discounts"
                        checked={membershipTypeForm.gunRentalAllowed}
                        onChange={(event) => setMembershipTypeForm((current) => ({ ...current, gunRentalAllowed: event.target.checked }))}
                      />
                      Allows rental discounts
                    </label>
                    {membershipTypeForm.gunRentalAllowed && (
                      <label>
                        Rental discount percent
                        <input
                          type="number"
                          min="0"
                          max="100"
                          aria-label="Rental discount percent"
                          value={membershipTypeForm.gunRentalDiscountPercent}
                          onChange={(event) => setMembershipTypeForm((current) => ({ ...current, gunRentalDiscountPercent: event.target.value }))}
                        />
                      </label>
                    )}
                  </div>
                  </TaskDisclosure>
                </div>

                {planSaveIssues.length > 0 && (
                  <div className="notice plan-form-guardrail">
                    <strong>Complete the required plan fields before saving.</strong>
                    <span>{planSaveIssues[0]}</span>
                  </div>
                )}

                <div className="button-row">
                  <button type="submit" disabled={planSaveDisabled}>
                    {savingMembershipType ? "Saving..." : editingMembershipTypeId ? "Save plan" : "Add plan"}
                  </button>
                  {editingMembershipType && editingMembershipType.status === "active" && editingMembershipType.membershipTypeId !== defaultMembershipTypeId && (
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => onSetDefaultMembershipType(editingMembershipType.membershipTypeId)}
                    >
                      Make default
                    </button>
                  )}
                  {editingMembershipType && editingMembershipType.status === "active" && editingMembershipType.membershipTypeId !== defaultMembershipTypeId && (
                    <button
                      type="button"
                      className="danger-button"
                      disabled={archivingMembershipTypeId === editingMembershipType.membershipTypeId}
                      onClick={() => onArchiveMembershipType(editingMembershipType)}
                    >
                      {archivingMembershipTypeId === editingMembershipType.membershipTypeId ? "Deactivating..." : "Deactivate plan"}
                    </button>
                  )}
                </div>
              </form>
          </div>
          )}

          <div className="table-like membership-plan-table">
            <div className="table-like-header">
              <span>Type</span>
              <span>Billing</span>
              <span>Price</span>
              <span>Access</span>
              <span>Actions</span>
            </div>
            {membershipTypes.map((type) => (
              <div className="table-like-row" key={type.membershipTypeId}>
                <span>
                  {type.name}
                  {type.membershipTypeId === defaultMembershipTypeId ? " (default)" : ""}
                  <small>
                    {type.publicSignupEnabled ? "Public signup" : "Owner-managed"} / {type.status}
                    {type.campaign?.enabled && type.campaign?.maxSignups ? ` / ${type.campaign.label || "limited campaign"}: ${type.campaign.maxSignups} spots` : ""}
                    {chaptersEnabled && type.chapterScope?.mode === "selected"
                      ? ` / ${type.chapterScope.chapterIds?.length || 0} chapter${type.chapterScope.chapterIds?.length === 1 ? "" : "s"}`
                      : ""}
                  </small>
                </span>
                <span>{formatMembershipBilling(type)} / {formatMembershipTerm(type)}</span>
                <span>
                  {type.pricing?.amountCents
                    ? `$${(type.pricing.amountCents / 100).toFixed(2)}`
                    : "Not set"}
                </span>
                <span>
                  {type.guestPassPolicy?.enabled ? `${type.guestPassPolicy.passes} guest passes` : "No guest passes"}
                  {type.accessRules?.laneReservation?.allowed ? " / lanes" : ""}
                </span>
                <span className="table-row-actions">
                  <button
                    type="button"
                    onClick={() => {
                      onEditMembershipType(type);
                      setPlanEditorExpanded(true);
                    }}
                  >
                    Edit
                  </button>
                </span>
              </div>
            ))}
          </div>

          </>
          )}

          {activeMembershipSection === "members" && (
          <div className="member-management-grid">
          <div className="member-list">
              <div className="panel-header">
                <h3>Members</h3>
                <p>Current member records for this range.</p>
              </div>
              {renderMemberDetail()}
              {!selectedMemberDetailId && (
              <>
              <div className="member-roster-command-bar">
                <div>
                  <strong>Roster actions</strong>
                  <span>Add a guest record or import a legacy roster without leaving the member list.</span>
                </div>
                <div className="row-actions">
                  <button
                    type="button"
                    className={memberRosterAction === "add" ? "is-active" : ""}
                    onClick={() => setMemberRosterAction((current) => current === "add" ? "" : "add")}
                  >
                    Add member
                  </button>
                  <button
                    type="button"
                    className={memberRosterAction === "import" ? "is-active" : ""}
                    onClick={() => setMemberRosterAction((current) => current === "import" ? "" : "import")}
                  >
                    Import roster
                  </button>
                </div>
              </div>
              {memberRosterAction === "add" && (
                <form className="member-inline-action-panel" onSubmit={onCreateMember}>
                  <div className="member-inline-action-header">
                    <div>
                      <h4>Add member</h4>
                      <p>Create a basic manual record for a guest or roster correction.</p>
                    </div>
                    <button type="button" className="secondary-button" onClick={() => setMemberRosterAction("")}>
                      Cancel
                    </button>
                  </div>
                  <div className="form-row">
                    <label>
                      Full name
                      <input
                        value={createMemberForm.fullName}
                        onChange={(event) => setCreateMemberForm((current) => ({ ...current, fullName: event.target.value }))}
                        placeholder="Alex Morgan"
                        required
                      />
                    </label>
                    <label>
                      Email
                      <input
                        type="email"
                        value={createMemberForm.email}
                        onChange={(event) => setCreateMemberForm((current) => ({ ...current, email: event.target.value }))}
                        placeholder="alex@example.com"
                      />
                    </label>
                    <label>
                      Membership type
                      <select
                        value={createMemberForm.membershipTypeId}
                        onChange={(event) => setCreateMemberForm((current) => ({ ...current, membershipTypeId: event.target.value }))}
                      >
                        {activeMembershipTypes.map((type) => (
                          <option value={type.membershipTypeId} key={type.membershipTypeId}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    {chaptersEnabled && (
                      <label>
                        Home chapter
                        <select
                          value={createMemberForm.homeChapterId || ""}
                          onChange={(event) => setCreateMemberForm((current) => ({ ...current, homeChapterId: event.target.value }))}
                        >
                          <option value="">No chapter</option>
                          {chapters.map((chapter) => (
                            <option value={chapter.chapterId} key={chapter.chapterId}>
                              {chapterLabel(chapter)}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label>
                      Status
                      <select
                        value={createMemberForm.status}
                        onChange={(event) => setCreateMemberForm((current) => ({ ...current, status: event.target.value }))}
                      >
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="expired">Expired</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </label>
                  </div>
                  <div className="form-actions">
                    <button type="submit" disabled={savingMember || !createMemberForm.fullName.trim()}>
                      {savingMember ? "Adding..." : "Add member"}
                    </button>
                  </div>
                </form>
              )}
              {memberRosterAction === "import" && (
                <form
                  className="member-inline-action-panel"
                  onSubmit={(event) => {
                    event.preventDefault();
                    onPreviewMemberImport(memberImportCsv);
                  }}
                >
                  <div className="member-inline-action-header">
                    <div>
                      <h4>Import roster</h4>
                      <p>Preview a legacy roster before creating member records.</p>
                    </div>
                    <button type="button" className="secondary-button" onClick={() => setMemberRosterAction("")}>
                      Cancel
                    </button>
                  </div>
                  <label>
                    CSV file
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={async (event) => {
                        const file = event.target.files?.[0];
                        if (file) {
                          setMemberImportCsv(await file.text());
                        }
                      }}
                    />
                  </label>
                  <label>
                    CSV contents
                    <textarea
                      rows="5"
                      value={memberImportCsv}
                      onChange={(event) => setMemberImportCsv(event.target.value)}
                      placeholder="fullName,email,plan,status,expirationDate,notes"
                    />
                    <small>Use the plan name shown in RangeGrid when possible; the preview will flag rows that need plan review.</small>
                  </label>
                  <div className="form-actions">
                    <button type="submit" disabled={memberImportLoading || !memberImportCsv.trim()}>
                      {memberImportLoading ? "Previewing..." : "Preview import"}
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={memberImportCommitting || !memberImportPreview?.validRows}
                      onClick={onCommitMemberImport}
                    >
                      {memberImportCommitting ? "Importing..." : `Import ${memberImportPreview?.validRows || 0} valid rows`}
                    </button>
                  </div>
                  {memberImportPreview && (
                    <div className="import-preview-panel">
                      <strong>
                        {memberImportPreview.validRows} valid / {memberImportPreview.invalidRows} invalid / {memberImportPreview.warningRows} warnings
                      </strong>
                      <div className="import-preview-list">
                        {memberImportPreview.rows.slice(0, 8).map((row) => (
                          <div className={row.valid ? "is-valid" : "is-invalid"} key={row.rowNumber}>
                            <strong>Row {row.rowNumber}: {row.member.fullName || "Missing name"}</strong>
                            <span>{row.member.email || "No email"} / {membershipTypeName(row.member.membershipTypeId)}</span>
                            {row.errors.length > 0 && <span>{row.errors.join("; ")}</span>}
                            {row.warnings.length > 0 && <span>{row.warnings.join("; ")}</span>}
                          </div>
                        ))}
                      </div>
                      {memberImportPreview.rows.length > 8 && (
                        <span>{memberImportPreview.rows.length - 8} more rows hidden in preview.</span>
                      )}
                    </div>
                  )}
                </form>
              )}
              <div className="form-grid compact-form-grid">
                <label>
                  Status
                  <select
                    value={memberFilters.status}
                    onChange={(event) => setMemberFilters((current) => ({ ...current, status: event.target.value }))}
                  >
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="expired">Expired</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="archived">Archived</option>
                  </select>
                </label>
                <label>
                  Plan
                  <select
                    value={memberFilters.membershipTypeId}
                    onChange={(event) => setMemberFilters((current) => ({ ...current, membershipTypeId: event.target.value }))}
                  >
                    <option value="">All plans</option>
                    {membershipTypes.map((type) => (
                      <option key={type.membershipTypeId} value={type.membershipTypeId}>{type.name}</option>
                    ))}
                  </select>
                </label>
                {chaptersEnabled && (
                  <label>
                    Chapter
                    <select
                      value={memberFilters.chapterId || ""}
                      onChange={(event) => setMemberFilters((current) => ({ ...current, chapterId: event.target.value }))}
                    >
                      <option value="">All chapters</option>
                      {chapters.map((chapter) => (
                        <option key={chapter.chapterId} value={chapter.chapterId}>{chapterLabel(chapter)}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  Source
                  <select
                    value={memberFilters.source}
                    onChange={(event) => setMemberFilters((current) => ({ ...current, source: event.target.value }))}
                  >
                    <option value="">All sources</option>
                    <option value="owner_manual">Manual entry</option>
                    <option value="public_membership_signup">Public signup</option>
                    <option value="csv_import">CSV import</option>
                  </select>
                </label>
                <label>
                  Search
                  <input
                    value={memberFilters.q}
                    onChange={(event) => setMemberFilters((current) => ({ ...current, q: event.target.value }))}
                    placeholder="Name, email, phone, notes"
                  />
                </label>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={!memberFilters.status && !memberFilters.membershipTypeId && !memberFilters.chapterId && !memberFilters.source && !memberFilters.q}
                  onClick={() => setMemberFilters({ status: "", membershipTypeId: "", chapterId: "", source: "", q: "" })}
                >
                  Clear filters
                </button>
              </div>
              <div className="form-actions member-export-actions">
                <button
                  type="button"
                  className="secondary-button"
                  disabled={memberExportBusy || visibleMembers.length === 0}
                  onClick={() => setMemberExportChooserOpen((current) => !current)}
                >
                  {memberExportBusy ? "Exporting..." : "Export member records"}
                </button>
              </div>
              {memberExportChooserOpen && (
                <div className="export-chooser-panel" role="dialog" aria-label="Member export options">
                  <div className="panel-header">
                    <div>
                      <h4>Choose member export</h4>
                      <p>Select the member dataset you want to review outside RangeGrid.</p>
                    </div>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setMemberExportChooserOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                  <div className="export-option-list">
                    <button
                      type="button"
                      className="export-option-button"
                      disabled={exportingMembers || visibleMembers.length === 0}
                      onClick={onExportMembers}
                    >
                      <strong>{exportingMembers ? "Exporting..." : "Member roster"}</strong>
                      <span>Names, emails, plan, status, term, source, and member notes.</span>
                    </button>
                    <button
                      type="button"
                      className="export-option-button"
                      disabled={exportingMemberQualifications || visibleMembers.length === 0}
                      onClick={onExportMemberQualifications}
                    >
                      <strong>{exportingMemberQualifications ? "Exporting..." : "Qualifications"}</strong>
                      <span>Qualification keys, labels, statuses, issue dates, expiry dates, and review notes.</span>
                    </button>
                    <button
                      type="button"
                      className="export-option-button"
                      disabled={exportingMemberFlags || visibleMembers.length === 0}
                      onClick={onExportMemberFlags}
                    >
                      <strong>{exportingMemberFlags ? "Exporting..." : "Internal flags"}</strong>
                      <span>Safety, account, incident, and admin flags for internal operations.</span>
                    </button>
                    <button
                      type="button"
                      className="export-option-button"
                      disabled={exportingMemberHousehold || visibleMembers.length === 0}
                      onClick={onExportMemberHousehold}
                    >
                      <strong>{exportingMemberHousehold ? "Exporting..." : "Household members"}</strong>
                      <span>Linked family or household member records and emergency contact context.</span>
                    </button>
                    <button
                      type="button"
                      className="export-option-button"
                      disabled={exportingMemberGuestPasses || visibleMembers.length === 0}
                      onClick={onExportMemberGuestPasses}
                    >
                      <strong>{exportingMemberGuestPasses ? "Exporting..." : "Guest passes"}</strong>
                      <span>Guest pass grants, use, expiration, and balance history.</span>
                    </button>
                  </div>
                </div>
              )}

              {membersLoading && <div className="member-refresh-strip">Refreshing members...</div>}
              {!membersLoading && hiddenDemoMemberCount > 0 && (
                <div className="access-demo-filter-note">
                  {hiddenDemoMemberCount} non-public member record{hiddenDemoMemberCount === 1 ? "" : "s"} hidden from this view.
                </div>
              )}
              {!membersLoading && activeMembers.length === 0 && (
                <div className="empty-state">No members yet.</div>
              )}
              {activeMembers.length > 0 && (
                <div className="member-table">
                  {activeMembers.map((member) => {
                    const type = membershipTypes.find((item) => item.membershipTypeId === member.membershipTypeId);
                    return (
                      <div
                        className={`member-row ${selectedMemberDetailId === member.memberId ? "is-selected" : ""}`}
                        key={member.memberId}
                      >
                        <div>
                          <strong>{member.fullName}</strong>
                          <span>{member.email || "No email"}</span>
                        </div>
                        <span>{type?.name || membershipTypeName(member.membershipTypeId)}</span>
                        {chaptersEnabled && (
                          <span>
                            <strong>{member.chapterSnapshot?.name || chapterNameById(chapters, member.homeChapterId) || "No chapter"}</strong>
                            <small>Home chapter</small>
                          </span>
                        )}
                        <span>
                          <strong className={`member-status status-${member.expiration?.effectiveStatus || member.status}`}>
                            {member.expiration?.effectiveStatus || member.status}
                          </strong>
                          <small>
                            {member.suspension
                              ? `Suspended: ${member.suspension.reason || "No reason"}`
                              : formatExpiration(member)}
                          </small>
                          {member.suspension?.endsAt && <small>Ends {formatDateOnly(member.suspension.endsAt)}</small>}
                        </span>
                        <span>
                          <strong>{formatGuestPassBalance(member.guestPassLedger)}</strong>
                          <small>Guest passes</small>
                        </span>
                        <div className="row-actions">
                          <button
                            type="button"
                            disabled={memberDetailLoading && selectedMemberDetailId === member.memberId}
                            onClick={() => onOpenMemberDetail(member.memberId)}
                          >
                            {selectedMemberDetailId === member.memberId ? "Selected" : "Details"}
                          </button>
                          {member.status !== "suspended" && member.status !== "archived" && (
                            <button
                              type="button"
                              disabled={updatingMemberStatusId === member.memberId}
                              onClick={() => quickStatusChange(member, "suspended", "Owner suspended member access")}
                            >
                              Suspend
                            </button>
                          )}
                          {member.status !== "active" && member.status !== "archived" && (
                            <button
                              type="button"
                              disabled={updatingMemberStatusId === member.memberId}
                              onClick={() => quickStatusChange(member, "active", "Owner reactivated member")}
                            >
                              Activate
                            </button>
                          )}
                          {member.status !== "archived" && (
                            <button
                              type="button"
                              className="danger-button"
                              disabled={updatingMemberStatusId === member.memberId}
                              onClick={() => quickStatusChange(member, "archived", "Owner archived member record")}
                            >
                              Archive
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              </>
              )}
            </div>
          </div>
          )}
        </>
      )}
    </section>
  );
}
